import '@kerfjs/ui/popup-menu/register';
import '@kerfjs/ui/select/register';
import '@kerfjs/ui/surface-scaffold/register';

import { closePopupMenu, openPopupMenuAt, type PopupMenuElement } from '@kerfjs/ui/popup-menu';
import { readSearchField } from '@kerfjs/ui/search-field';
import { openDialog } from '@kerfjs/ui/surface-scaffold';
import { wireContentItems } from '@kerfjs/ui/wire-content-items';
import { wireFilePickers } from '@kerfjs/ui/wire-file-pickers';
import { wireScrollDividers } from '@kerfjs/ui/wire-scroll-dividers';
import { wireSearchFields } from '@kerfjs/ui/wire-search-fields';
import { wireToolbarVisibility } from '@kerfjs/ui/wire-toolbar-visibility';
import { wireWorkbench } from '@kerfjs/ui/wire-workbench';
import { delegate, effect, mount } from 'kerfjs';
import { debounce } from 'kerfjs/timing';

import { isAwaitingSense, type NewsItem } from '../../db/schemas.js';
import { topicsForProfiles } from '../../profile-topics.js';
import { PROFILE_PAGE_COUNT } from '../../profiles.js';
import { FocusIdsSchema } from '../../topic-focus.js';
import type { DialogKey } from '../components/dialogs/dialogs.js';
import { ALL_FILTER } from '../components/filter-bar.js';
import { rootJsx, WORKBENCH_ID } from '../components/reader-shell.js';
import type { TunerState } from '../features/discovery/discover.js';
import { judgeCandidate, mergeKept, nextRound, startTuner } from '../features/discovery/discover.js';
import { recordDuration } from '../features/discovery/discover-progress.js';
import { shareItem } from '../features/feed/share.js';
import { feedViewKey } from '../features/feed/view-filters.js';
import { ensureNotificationPermission } from '../features/notifications/notifications.js';
import { disablePush, enablePush } from '../features/notifications/push.js';
import { newlyPickedProfiles, nextProfilePage, shouldOpenOnboarding } from '../features/onboarding/onboarding.js';
import { sessionGateState, signedOutSession, signInApple, signInGoogle } from '../features/session/session.js';
import { importPickedFile } from '../features/settings/import-files.js';
import { kindCapacity } from '../features/topics/capacity.js';
import { toggleSolo, transferSingleSolo } from '../features/topics/solo.js';
import { sortTopics } from '../features/topics/topic-sort.js';
import { syncColorScheme } from '../lib/color-scheme.js';
import { browserPollDeps, startPolling as startStatePolling } from '../lib/poll.js';
import { syncRadios, syncSelects } from '../lib/select-sync.js';
import { startSignalTransport } from '../lib/signal-transport.js';
import {
  addSuggestedTopic,
  addTopic,
  chooseTopicSense,
  clearAllStories,
  countItemsForTopic,
  deleteAllTopics,
  deleteTopic,
  discoverTopics,
  dismissQuarantine,
  enableTopic,
  fetchSession,
  importStories,
  importTopics,
  loadPulseDetail,
  loadThread,
  logoutSession,
  refreshFeed,
  refreshPulseSurfaces,
  refreshState,
  registerAccount,
  renameTopic,
  reportForeground,
  restoreClearedItems,
  setItemOffTopic,
  setItemSaved,
  setNotifyOnNewItems,
  setTopicPaused,
  submitUpgrade,
  suggestSimilar,
  swapCapacity,
  updateLocation,
  updateProfiles,
  updateTheme,
} from './api.js';
import { apiBase } from './config.js';
import type { DiscoverSource, OnboardingStep, ToastState } from './stores.js';
import {
  appStore,
  ONBOARDING_STEPS,
  pollTick,
  railCollapsed,
  readOnboardingAutoOpenSuppressed,
  setRailCollapsed,
  TOPIC_SORTS,
  writeOnboardingSeen,
} from './stores.js';
import { schemeDark, SEARCH_FIELD_ID, searchExpanded, searchRevision } from './view-state.js';

/** How long a toast stays up before it fades out on its own. */
const TOAST_MS = 2600;
/**
 * How long a toast that offers an **undo** stays up (NEWS-145).
 *
 * Longer than a plain one, because the two ask different things of the reader.
 * A plain toast only has to be *noticed*; this one has to be read, understood as
 * reversible, and acted on with the mouse. 2.6s is enough for the first and not
 * the second, and a window that expires mid-reach is worse than no undo at all —
 * it teaches that the affordance is unreliable.
 *
 * Comfortably inside the server's `UNDO_TTL_MS`, so the button is never on
 * screen after the snapshot behind it has gone.
 */
const UNDO_TOAST_MS = 9000;
let toastTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * The control focus returns to when Settings closes (NM-94).
 *
 * Dialogs trapped Tab but never returned focus to whatever
 * opened them — so closing the sheet dropped focus onto `<body>`, and a keyboard
 * or screen-reader user lost their place on the page. It matters most on the
 * mobile sheet, which fills the screen. Captured when Settings opens, restored on
 * every close path (button, backdrop, Escape) through `closeSettings`.
 */
let settingsOpener: HTMLElement | null = null;

/** Close Settings and return focus to whatever opened it (NM-94). */
function closeSettings(): void {
  appStore.actions.setSettingsOpen(false);
  const opener = settingsOpener;
  settingsOpener = null;
  // After the re-render that removes the dialog, like the settings-tab focus
  // move at the keyboard handler below — the opener is outside the dialog and so
  // survives, but focusing before the morph can be undone by it.
  if (opener !== null) {
    queueMicrotask(() => {
      opener.focus();
    });
  }
}

/**
 * Debounce for the server-side feed search refetch (NEWS-76). kerf's `debounce`
 * (kerfjs 4.2, NM-98) is trailing-edge with `.cancel()` — it replaces the
 * hand-rolled `clearTimeout`/`setTimeout` pair and the top-level `let` it needed.
 */
const SEARCH_DEBOUNCE_MS = 250;
const debouncedRefreshFeed = debounce(() => void refreshFeed(), SEARCH_DEBOUNCE_MS);

/** Show a transient bottom-of-screen notice, replacing any current one. */
/**
 * Show a message that dismisses itself.
 *
 * The **only** sanctioned way to raise a toast: the store's `setToastRaw` has
 * no timer, so anything calling it directly leaves the message on screen for
 * good — which is exactly what shipped in NEWS-141.
 */
function showToast(message: string): void {
  raiseToast({ message, undoTopicId: null }, TOAST_MS);
}

/** Monotonic key source for optimistic pending-topic placeholders (NM-168). */
let pendingTopicSeq = 0;

/** A fresh placeholder key for one in-progress add (NM-168). */
function newPendingKey(): string {
  return `pending-${String((pendingTopicSeq += 1))}`;
}

/**
 * Create the topic for a placeholder that is **already showing** in the rail, and
 * reconcile that placeholder when the create settles (NM-184). Split out of
 * `submitAddTopic` so the form submit can raise the "Adding…" placeholder the
 * instant it is submitted — before the several-second did-you-mean check — and
 * this only runs the create once that check has decided not to interrupt.
 *
 * The placeholder is removed however the create settles — success (the real row
 * has already arrived through `addTopic`'s refresh, including an over-cap add that
 * lands disabled, NM-200), awaiting-sense, or a rejected create (a duplicate 409,
 * surfaced as a toast) — so it can never outlive its answer.
 */
function finishAddForPending(name: string, key: string): void {
  void addTopic(name)
    .then((topic) => {
      if (topic === null) return;
      // An ambiguous name comes back awaiting a sense (NM-163). Prompt for it
      // immediately — the user is right here having just typed it — by opening the
      // sense picker; its row also carries a "needs attention" marker for when the
      // dialog is dismissed or the tab was backgrounded on add.
      if (isAwaitingSense(topic)) {
        appStore.actions.openSensePicker(topic.id);
        return;
      }
      // Over the plan cap the subject is still added, but **disabled** (NM-200): it
      // comes back paused, showing in the Disabled group with no articles. Tell the
      // user it landed off and where to find it, rather than letting it appear to
      // vanish. A fresh add is only paused when it was capped.
      if (topic.paused) {
        showToast('Added, but off — you’re at your plan’s limit. Find it in the Disabled group.');
      }
    })
    .catch((err: unknown) => {
      showToast(err instanceof Error ? err.message : 'Could not add that topic.');
    })
    .finally(() => {
      appStore.actions.removePendingTopic(key);
    });
}

/**
 * Add a topic directly, raising its own optimistic placeholder (NM-168). The path
 * for callers that have already decided on a name — a discovery suggestion, the
 * profile strip, a "did you mean?" correction or Follow. The typed-form path uses
 * `finishAddForPending` instead, so its placeholder can go up before the
 * did-you-mean check rather than after (NM-184).
 */
function submitAddTopic(name: string): void {
  const key = newPendingKey();
  appStore.actions.addPendingTopic({ key, name });
  finishAddForPending(name, key);
}

/**
 * The user chose a *different* name in the did-you-mean dialog than they typed — a
 * spelling correction or an existing subject to follow (NM-184). Close the dialog,
 * drop the placeholder that was standing in for the typed name, and add the chosen
 * name (which raises its own placeholder).
 */
function replacePendingAndAdd(name: string): void {
  const pendingKey = appStore.state.value.topicSuggest?.pendingKey ?? null;
  appStore.actions.setTopicSuggest(null);
  if (pendingKey !== null) appStore.actions.removePendingTopic(pendingKey);
  submitAddTopic(name);
}

/** Close the did-you-mean dialog and remove the placeholder it was deciding (NM-184). */
function dismissSuggest(): void {
  const pendingKey = appStore.state.value.topicSuggest?.pendingKey ?? null;
  appStore.actions.setTopicSuggest(null);
  if (pendingKey !== null) appStore.actions.removePendingTopic(pendingKey);
}

/** Show a message with an Undo for a topic's just-cleared stories (NEWS-145). */
function showUndoToast(message: string, topicId: string): void {
  raiseToast({ message, undoTopicId: topicId }, UNDO_TOAST_MS);
}

function raiseToast(toast: ToastState, ms: number): void {
  appStore.actions.setToastRaw(toast);
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    appStore.actions.setToastRaw(null);
  }, ms);
}

/** The download filename from a response's `Content-Disposition`, or null (NM-101). */
function filenameFromDisposition(res: Response): string | null {
  const cd = res.headers.get('content-disposition');
  if (cd === null) return null;
  const m = /filename="?([^"]+?)"?(?:;|$)/.exec(cd);
  return m?.[1] ?? null;
}

/** Save a blob to disk via a transient object-URL anchor (NM-101). */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * Fetch an export and save it, with progress + result feedback (NM-101).
 *
 * NEWS-157 downloaded through a native `<a download>`, whose whole lifecycle is
 * opaque to the page — so it could not disable the control while the export
 * generated, nor report success or failure. The owner's call was to trade the
 * native link's right-click-Save-As for that feedback: the click now fetches the
 * export, the control disables and reads "Exporting…" while `exporting` is true,
 * and a success or failure toast reports the outcome. The server's
 * `Content-Disposition` filename is preserved so the saved file is named as before.
 */
async function runExport(url: string): Promise<void> {
  if (appStore.state.value.exporting) return; // one export at a time
  appStore.actions.setExporting(true);
  try {
    // Prefix the API origin + send the cookie for a split-origin deploy (NM-188).
    const res = await fetch(apiBase() + url, { credentials: 'include' });
    if (!res.ok) throw new Error(`the server returned ${String(res.status)}`);
    const blob = await res.blob();
    const filename = filenameFromDisposition(res) ?? 'newsmonger-export';
    saveBlob(blob, filename);
    showToast(`Exported ${filename}`);
    // Close the stories export dialog on success (the topics export has no dialog).
    if (appStore.state.value.export !== null) appStore.actions.closeExport();
  } catch (err: unknown) {
    showToast(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
  } finally {
    appStore.actions.setExporting(false);
  }
}

/**
 * Show the confirmation dialog and resolve with the user's choice.
 *
 * The resolver lives at module scope rather than in the store: it's a callback,
 * not renderable state, and only ever one dialog is open. Opening a second
 * before the first resolves cancels the first, so no promise is left dangling.
 */
let confirmResolver: ((ok: boolean) => void) | null = null;
/** The danger-confirm countdown interval (NM-101), or null when none is armed. */
let confirmCountdown: ReturnType<typeof setInterval> | null = null;

function stopConfirmCountdown(): void {
  if (confirmCountdown !== null) {
    clearInterval(confirmCountdown);
    confirmCountdown = null;
  }
}

function confirm(
  message: string,
  opts: { confirmLabel?: string; danger?: boolean; title?: string; holdSeconds?: number } = {},
): Promise<boolean> {
  confirmResolver?.(false);
  stopConfirmCountdown();
  const holdSeconds = opts.holdSeconds ?? 0;
  appStore.actions.openConfirm({
    message,
    title: opts.title ?? null,
    confirmLabel: opts.confirmLabel ?? 'OK',
    danger: opts.danger ?? false,
    holdSeconds,
    remaining: holdSeconds,
  });
  // Arm the countdown: tick once a second until it reaches 0, then stop. The
  // dialog re-renders each tick (the store drives it), enabling the button at 0.
  if (holdSeconds > 0) {
    confirmCountdown = setInterval(() => {
      appStore.actions.tickConfirmCountdown();
      if ((appStore.state.value.confirm?.remaining ?? 0) <= 0) stopConfirmCountdown();
    }, 1000);
  }
  return new Promise<boolean>((resolve) => {
    confirmResolver = resolve;
  });
}

function resolveConfirm(ok: boolean): void {
  stopConfirmCountdown();
  appStore.actions.closeConfirm();
  const resolve = confirmResolver;
  confirmResolver = null;
  resolve?.(ok);
}

/**
 * Put the caret in the Add Topic field (NM-ZJT0H7). The dialog opens a frame
 * after the render that adds it (`syncDialogs`), so the kerf UI `TextField`'s
 * native `autofocus` alone can lose. Retry for a few frames until the field
 * holds focus (or the dialog has gone).
 */
function focusAddTopicField(root: HTMLElement, frames = 20): void {
  requestAnimationFrame(() => {
    if (!appStore.state.value.addTopicOpen) return;
    const field = root.querySelector<HTMLElement>('dialog[data-dialog=add-topic] input[name=topic-name]');
    if (field !== null && document.activeElement !== field) field.focus();
    if (document.activeElement !== field && frames > 0) focusAddTopicField(root, frames - 1);
  });
}

function openTopicSortMenu(trigger: Element): void {
  const menu = trigger.closest('[data-rail-header]')?.querySelector<PopupMenuElement>('[data-topic-sort-menu]');
  if (menu === undefined || menu === null) return;
  const rect = trigger.getBoundingClientRect();
  openPopupMenuAt(menu, rect.left, rect.bottom);
}

/** Anchor for shift-range selection — the last row clicked without shift. */
let anchorId: string | null = null;

/** Preserve double-click's additive toggle when its first click transferred solo. */
let clickSoloTransfer: { id: string; prior: string[] } | null = null;

function selectTopic(id: string, mods: { toggle: boolean; range: boolean }): void {
  const { topics, selectedTopicIds, topicSort } = appStore.state.value;
  if (mods.range && anchorId !== null) {
    // Range is over the *displayed* order, which the sort determines (NEWS-63).
    const ids = sortTopics(topics, topicSort, appStore.state.value.newestItemAtByTopic).map((t) => t.id);
    const from = ids.indexOf(anchorId);
    const to = ids.indexOf(id);
    if (from !== -1 && to !== -1) {
      const [lo, hi] = from < to ? [from, to] : [to, from];
      appStore.actions.setSelection(ids.slice(lo, hi + 1));
      return;
    }
  }
  if (mods.toggle) {
    const next = selectedTopicIds.includes(id) ? selectedTopicIds.filter((x) => x !== id) : [...selectedTopicIds, id];
    appStore.actions.setSelection(next);
    anchorId = id;
    return;
  }
  appStore.actions.setSelection([id]);
  anchorId = id;
}

/** Prompt for and delete `ids`, naming what's about to go. */
function confirmDelete(ids: string[]): void {
  const { topics } = appStore.state.value;
  const names = topics.filter((t) => ids.includes(t.id)).map((t) => t.name);
  if (names.length === 0) return;
  const what = names.length === 1 ? `\u201c${names[0] ?? ''}\u201d` : `${String(names.length)} topics`;
  void (async () => {
    if (!(await confirm(`Delete ${what} and all of their stories?`, { confirmLabel: 'Delete', danger: true }))) return;
    appStore.actions.setSelection([]);
    for (const id of ids) await deleteTopic(id);
  })();
}

/** Every story the client currently holds: the server feed page plus the
 *  just-flagged session overlay (NEWS-76) — the pool for id lookups. */
function feedAndFlagged(): NewsItem[] {
  const s = appStore.state.value;
  return [...s.feedItems, ...s.recentlyFlaggedItems];
}

/** Share one story, toasting only when it fell back to the clipboard/failed. */
async function shareOne(item: NewsItem): Promise<void> {
  const result = await shareItem(item);
  // The OS share sheet is its own feedback, and a cancelled share needs none.
  if (result === 'copied') showToast('Copied to clipboard');
  else if (result === 'failed') showToast("Couldn't share this story");
}

/** Flag/unflag a story off-topic (NEWS-61). Flagging holds the item this session
 *  (via `recentlyFlaggedItems`) so it stays visible-but-collapsed after the
 *  server drops it from the normal page; unflagging releases it (NEWS-76). */
function flagItem(item: NewsItem, offTopic: boolean): void {
  if (offTopic) appStore.actions.addRecentlyFlagged(item);
  else appStore.actions.removeRecentlyFlagged(item.id);
  void setItemOffTopic(item.id, offTopic).then(refreshFeed);
}

/**
 * Open the topic menu anchored to a row rather than to a pointer (NEWS-90).
 *
 * The mouse path positions the menu at the cursor; a keyboard has no cursor, so
 * it is anchored to the row's own box. Shared with the mouse handler's
 * selection rules so the two can't drift apart.
 */
/**
 * Open a topic row's action menu at a viewport point. Right-clicking outside
 * the selection selects that row first, so the menu never acts on rows the
 * user can't see are targeted; the menu opens on the next frame, after the
 * re-render has relabelled it for what it now acts on.
 */
function openTopicMenuAt(row: Element, id: string, x: number, y: number): void {
  const current = appStore.state.value.selectedTopicIds;
  if (!current.includes(id)) {
    appStore.actions.setSelection([id]);
    anchorId = id;
  }
  requestAnimationFrame(() => {
    const live = document.querySelector(`[data-topic-row="${CSS.escape(id)}"]`) ?? row;
    const menu = live.querySelector<PopupMenuElement>('[data-component="popup-menu"]');
    if (menu !== null) openPopupMenuAt(menu, x, y);
  });
}

/** Dismiss the first-run flow and remember that it has been seen. */
function closeOnboarding(): void {
  const s = appStore.state.value;
  appStore.actions.setOnboarding(null);
  writeOnboardingSeen(s.installId, s.session?.user?.id ?? '');
}

/**
 * Move to a step, doing whatever that step needs on the way in.
 *
 * One place rather than one branch per caller: the profile picker and the
 * ordinary Continue both arrive here.
 */
function advanceOnboardingTo(next: OnboardingStep): void {
  appStore.actions.setOnboarding(next);
  // Seed the picker from what is already saved, so reopening the guide from
  // Settings shows previous picks ticked rather than a blank grid (NEWS-383).
  if (next === 'profiles') {
    appStore.actions.seedOnboardingProfiles(appStore.state.value.settings.profiles);
  }
}

/** Whether an arbitrary string is one of the wizard's steps (NM-246 dot nav). */
function isOnboardingStep(value: string): value is OnboardingStep {
  return (ONBOARDING_STEPS as readonly string[]).includes(value);
}

/** Step back one level from the current step (NM-246 Back button). */
function goBackOneOnboardingStep(): void {
  const current = appStore.state.value.onboarding;
  if (current === null || current === 'auto') return;
  const prev = ONBOARDING_STEPS.indexOf(current) - 1;
  if (prev < 0) return;
  const step = ONBOARDING_STEPS.at(prev);
  if (step !== undefined) advanceOnboardingTo(step);
}

/**
 * Finish the flow from the last step: create everything picked this run, then
 * close (NEWS-146, NM-244/248).
 *
 * Extracted so both the primary button and — on the last step only — the Escape
 * ladder can commit it: closing the last screen "in any way" means Start watching
 * / Keep current, never discard (NM-245).
 *
 * Only the ticked starter chips and the interests picked **this run** become
 * topics. Anything from discovery already exists (created when it was added, with
 * its classification and narrowed first check). `newlyPickedProfiles` is what
 * makes a re-run add only the new interests, and an unchanged re-run add nothing.
 * Topics are added one at a time because each POST fires its own first check.
 */
function finishOnboarding(): void {
  const state = appStore.state.value;
  const newProfiles = newlyPickedProfiles(state.settings.profiles, state.onboardingProfilesAtOpen);
  // The ticked chips win any overlap with a profile-derived name: a name the user
  // picked themselves is a stronger signal. `exclude` also carries what already
  // exists, so a re-run cannot propose something already watched.
  const fromProfiles = topicsForProfiles(newProfiles, {
    exclude: [...state.onboardingTopics, ...state.topics.map((t) => t.name)],
  });
  const chosen = [...state.onboardingTopics, ...fromProfiles];
  closeOnboarding();
  void (async () => {
    for (const name of chosen) {
      await addTopic(name);
    }
  })();
}

/**
 * Save the ticked profiles, then leave the picker.
 *
 * Saves **before** advancing, and advances regardless of whether the write
 * succeeded: a failed PATCH here should not trap the user in setup. The value is
 * a preference, not a precondition for anything later in the flow.
 */
async function saveProfilesAndAdvance(): Promise<void> {
  const { onboardingProfiles } = appStore.state.value;
  // `.at()` is undefined past the end, so a reorder that left `profiles` last
  // closes the guide rather than advancing to nothing.
  const next = ONBOARDING_STEPS.at(ONBOARDING_STEPS.indexOf('profiles') + 1);
  try {
    await updateProfiles(onboardingProfiles);
  } finally {
    if (next === undefined) closeOnboarding();
    else advanceOnboardingTo(next);
  }
}

/**
 * Open the first-run flow for someone who has not set the app up yet.
 *
 * **Having no topics is the whole test** (NEWS-421). It used to also require no
 * usable provider, on the reasoning that someone who had either was an existing
 * user who must not be interrupted. The topic count alone already says that —
 * an existing user has topics — and the provider half was actively harmful: a
 * signed-in `claude-cli` is a fact about the *machine*, established before this
 * app was ever installed, and says nothing about whether Newsmonger is set up.
 *
 * So anyone who already used Claude Code or Codex — which FR-20.5 treats as the
 * *best* case, presenting a detected subscription first — got no setup guide at
 * all, ever. The condition excluded exactly the audience the flow was written
 * for.
 *
 * Gated only on `/api/state` having answered (NM-12): provider configuration is
 * environment-only now and there is no Source step, so there is no provider list
 * to wait for. `topics` is empty before the first load too, so `loaded` is what
 * stops the wizard flashing at every existing user on every reload.
 *
 * Opens whenever there are no topics, **every session** (NM-222): dismissal is
 * per-session only (the `onboarding` state goes to `null`, which this guard sees
 * as "not auto" and leaves shut until the next reload resets it to `'auto'`), so
 * it is not remembered across launches. The persisted per-install "seen" flag no
 * longer suppresses the auto-open — it only labels the Settings button (FR-20.3).
 * `suppressed` is the test-only escape hatch the E2E fixture seeds.
 */
/** The rail's "Upgrade plan" button (NM-226), beside the plan-usage counts. */
function maybeOpenOnboarding(): void {
  const s = appStore.state.value;
  if (s.onboarding !== 'auto') return;
  if (!s.loaded) return;
  const open = shouldOpenOnboarding({
    loaded: s.loaded,
    topicCount: s.topics.length,
    suppressed: readOnboardingAutoOpenSuppressed(),
  });
  appStore.actions.setOnboarding(open ? 'welcome' : null);
}

/**
 * Fly a disabled topic's row down to the bottom of the screen (NM-241).
 *
 * Disabling a topic moves it to the collapsed **Disabled** group at the foot of
 * the rail (FR-48.6), which otherwise just vanishes from its place in the list.
 * This animates the departure: a fixed-position **clone** of the row lifts off
 * where it sat and slides down toward the bottom, shrinking and fading, so the
 * eye follows it to where it went. A clone rather than the row itself, because
 * the row is about to be re-rendered out of the active list by the state update
 * the disable triggers — animating a node the morph is removing is a race; a
 * throwaway copy owned by nothing is not. Skipped under `prefers-reduced-motion`
 * (the disable is instant), and a no-op if the row isn't on screen.
 */
function flyTopicRowToBottom(id: string): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const row = document.querySelector<HTMLElement>(`[data-topic-row="${CSS.escape(id)}"]`);
  if (row === null) return;
  const rect = row.getBoundingClientRect();
  const clone = row.cloneNode(true) as HTMLElement;
  // Not a real row: keep it out of the delegates, the a11y tree, and every
  // `[data-topic-row]` / state selector, so a locator (the app's and the test
  // suite's) can't match this throwaway copy while it flies. It keeps the kerf
  // UI row's own classes, so it still looks like the row it was; its menu is
  // dropped, so no second live dropdown exists for the length of the flight.
  for (const attr of [...clone.attributes]) {
    if (attr.name.startsWith('data-topic') || attr.name === 'data-key') clone.removeAttribute(attr.name);
  }
  clone.querySelector('[data-component="popup-menu"]')?.remove();
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('inert', '');
  clone.setAttribute('data-flyaway', '');
  Object.assign(clone.style, {
    position: 'fixed',
    left: `${String(Math.round(rect.left))}px`,
    top: `${String(Math.round(rect.top))}px`,
    width: `${String(Math.round(rect.width))}px`,
    margin: '0',
    zIndex: '40',
    pointerEvents: 'none',
  });
  document.body.appendChild(clone);
  const remove = (): void => {
    clone.remove();
  };
  // Next frame, so the start transform is committed before the transition begins.
  requestAnimationFrame(() => {
    const drop = window.innerHeight - rect.top - Math.round(rect.height * 0.3);
    clone.style.transition = 'transform 460ms cubic-bezier(0.4, 0.05, 0.25, 1), opacity 460ms ease-in';
    clone.style.transform = `translateY(${String(drop)}px) scale(0.72)`;
    clone.style.opacity = '0';
  });
  clone.addEventListener('transitionend', remove, { once: true });
  // Belt-and-braces: if `transitionend` never fires (interrupted), clean up anyway.
  window.setTimeout(remove, 700);
}

/** Apply a context-menu action to every targeted topic. */
function runTopicAction(action: string, ids: string[]): void {
  const { topics, soloTopicIds } = appStore.state.value;
  const targets = topics.filter((t) => ids.includes(t.id));
  switch (action) {
    case 'pause': {
      // Enable/disable (NM-201). Mixed selections resolve toward the action that
      // changes the most rows, matching the label the menu showed.
      const disable = targets.some((t) => !t.paused);
      if (disable) {
        // Disabling never hits the cap — it only frees room.
        for (const t of targets) {
          if (t.paused) continue;
          // Fly the row down to the Disabled group before the state update
          // re-renders it out of the active list (NM-241).
          flyTopicRowToBottom(t.id);
          void setTopicPaused(t.id, true);
        }
      } else {
        // Enabling is capacity-gated: the first over-cap one opens the dialog.
        void enableTopicsWithCapacity(targets.filter((t) => t.paused).map((t) => t.id));
      }
      break;
    }
    case 'solo':
      appStore.actions.setSolo(
        toggleSolo(
          soloTopicIds,
          targets.map((t) => t.id),
        ),
      );
      void refreshFeed();
      void refreshPulseSurfaces();
      break;
    case 'rename': {
      // Single-target only: there is one name and one focus to edit.
      const only = targets.length === 1 ? targets[0] : undefined;
      if (only !== undefined) {
        appStore.actions.openRename(only.id);
        // The count is what decides whether clearing is even offered, and it is
        // fetched per dialog rather than polled for every topic (NEWS-139).
        void countItemsForTopic(only.id).then((count) => {
          if (appStore.state.value.renameTopicId === only.id) {
            appStore.actions.setRenameItemCount(count);
          }
        });
      }
      break;
    }
    case 'review-flagged':
      // Enter review mode for the targeted topics (the menu item is disabled
      // when none of them have flagged stories).
      appStore.actions.setReviewTopicIds(ids);
      void refreshFeed();
      break;
    case 'delete':
      confirmDelete(ids);
      break;
    default:
      break;
  }
}

/**
 * Enable a set of subjects, capacity-aware (NM-201). Enables each in turn; the
 * first one the plan cap refuses opens the pick-to-disable dialog for that kind,
 * carrying the refused id so the swap turns it on once room is freed. The rest (a
 * rare multi-select case) can be retried after. A different error already surfaced
 * in the banner via `enableTopic`.
 */
async function enableTopicsWithCapacity(ids: string[]): Promise<void> {
  for (const id of ids) {
    const over = await enableTopic(id);
    if (over !== null) {
      appStore.actions.openDisableCapacity(over.kind, [id]);
      return;
    }
  }
}

/** Commit the capacity dialog (NM-201): the atomic enable/disable swap. */
async function commitCapacity(): Promise<void> {
  const dialog = appStore.state.value.capacityDialog;
  if (dialog === null) return;
  const [enable, disable] =
    dialog.mode === 'disable' ? [dialog.enableIds, dialog.picked] : [dialog.picked, [] as string[]];
  const over = await swapCapacity(enable, disable);
  if (over === null) {
    appStore.actions.closeCapacity();
    return;
  }
  // The server rolled the swap back — the picked set still doesn't fit. Leave the
  // dialog open and say which kind is the problem.
  showToast(`Still over your limit for ${over.kind} topics — pick more to disable.`);
}

/**
 * Select as many disabled subjects as fit the free capacity, per kind (NM-201) —
 * the upgrade prompt's "select as many as fit". Fills each kind up to its room; an
 * unlimited kind takes them all.
 */
function selectAllCapacity(): void {
  const s = appStore.state.value;
  const dialog = s.capacityDialog;
  if (dialog === null || s.plan === null || dialog.mode !== 'enable') return;
  const customFree = kindCapacity(s.plan, 'custom').free;
  const catalogFree = kindCapacity(s.plan, 'catalog').free;
  const picked: string[] = [];
  let custom = 0;
  let catalog = 0;
  for (const t of s.topics.filter((topic) => topic.paused)) {
    if (t.catalog) {
      if (catalogFree === null || catalog < catalogFree) {
        picked.push(t.id);
        catalog += 1;
      }
    } else if (customFree === null || custom < customFree) {
      picked.push(t.id);
      custom += 1;
    }
  }
  appStore.actions.setCapacityPicked(picked);
}

/**
 * Issue-order guard for discovery responses (NEWS-324) — see `runDiscovery`.
 *
 * Module-scoped like `refreshState`'s counters, and for the same reason: there
 * is one dialog per page, so one pair of counters describes it.
 */
let discoverSeq = 0;
let discoverApplied = 0;

/**
 * Cancel any in-flight discovery response (NM-117): advance the applied counter
 * past every issued request id, so `runDiscovery`'s issue-order guard
 * (`seq < discoverApplied`) drops a response that arrives after the user pressed
 * Back. A subsequent real search issues a higher `seq` and applies normally.
 */
function cancelDiscoverInFlight(): void {
  discoverApplied = discoverSeq + 1;
}

/**
 * Run one discovery request and fold the answer into the pane (NEWS-126).
 *
 * The `discover === null` guards are the point of doing this in one place: the
 * user can close the dialog while a request is in flight, and a response that
 * reopened it — or wrote into a fresh session's state — is the kind of bug that
 * only shows up on a slow provider.
 */
async function runDiscovery(source: DiscoverSource): Promise<void> {
  // Discovery answers apply in **issue** order, not arrival order (NEWS-324).
  //
  // The same guard `refreshState` and `refreshFeed` carry (NEWS-104), and
  // **defence in depth rather than a live bug**: measured, the UI already
  // serialises these. While `loading` is true the submit button is disabled and
  // `discoverPaneJsx` replaces the whole body with the waiting state, so there
  // is no control on screen that can issue a second request. An attempt to
  // demonstrate the race through the UI could not — the second search never
  // fired, which is what `discoverWaitingJsx` is asserted to guarantee in
  // `discover.spec.ts`.
  //
  // Kept because that protection is *incidental*: it falls out of a rendering
  // decision, not a stated invariant. Keeping results on screen during a
  // re-search — a reasonable future change, and the sort of thing a design pass
  // asks for — would expose the race immediately, and the failure would be a
  // list of results for a query the heading no longer names, plus `source` and
  // `suggestions` disagreeing about which search they came from. Four lines to
  // make that impossible ahead of time is worth it; four lines pretending to fix
  // something reachable today would not be.
  //
  // `loadMoreSuggestions` guards itself by comparing `source`; that works for
  // *append* and cannot work here, because re-searching the same source is a
  // legitimate thing to do twice.
  const seq = ++discoverSeq;
  appStore.actions.patchDiscover({
    loading: true,
    error: null,
    view: 'results',
    source,
    // A new search is a fresh seam — clear whatever the previous one ended on.
    exhausted: false,
    loadingMore: false,
  });
  const startedAt = Date.now();
  try {
    const { suggestions, cached } = await discoverTopics(source);
    // Only a real call informs the estimate — a cache hit returns instantly and
    // would drag the next bar's target down to nothing (NEWS-137).
    if (!cached) recordDuration(Date.now() - startedAt);
    if (appStore.state.value.discover === null || seq < discoverApplied) return;
    discoverApplied = seq;
    appStore.actions.patchDiscover({ loading: false, suggestions, cached, view: 'results' });
  } catch (err) {
    // Gated too: a stale *failure* must not raise an error over results a newer,
    // successful request already applied.
    if (appStore.state.value.discover === null || seq < discoverApplied) return;
    discoverApplied = seq;
    // Shown inside the dialog next to a retry, not in the global banner: the
    // user is mid-task here, and the message is about this request.
    appStore.actions.patchDiscover({
      loading: false,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Fetch one tuner round and install it (NEWS-127).
 *
 * Every round is a billable call, so the server-side bound (FR-24.9) is the
 * backstop; this stops asking on its own once `judgeCandidate` says exhausted.
 */
async function fetchTunerRound(tuner: TunerState, advance: boolean): Promise<void> {
  appStore.actions.patchDiscover({ tuner: { ...tuner, loading: true, error: null } });
  try {
    const { suggestions } = await discoverTopics({
      kind: 'tune',
      anchor: tuner.anchor,
      direction: tuner.direction,
      kept: tuner.kept.map((s) => s.name),
      skipped: tuner.skipped,
      round: advance ? tuner.round + 1 : tuner.round,
    });
    // The user can close the dialog or end the session while a round is in
    // flight; installing it then would resurrect a tuner they walked away from.
    if (appStore.state.value.discover?.tuner === null) return;
    appStore.actions.patchDiscover({
      tuner: advance ? nextRound(tuner, suggestions) : { ...tuner, queue: suggestions, index: 0, loading: false },
    });
  } catch (err) {
    if (appStore.state.value.discover?.tuner === null) return;
    appStore.actions.patchDiscover({
      tuner: { ...tuner, loading: false, error: err instanceof Error ? err.message : String(err) },
    });
  }
}

/** Enter the tuner, scoped to a card or to the whole result set (FR-24.5). */
async function enterTuner(anchor: string, direction: 'narrower' | 'similar'): Promise<void> {
  const tuner = startTuner(anchor, direction);
  appStore.actions.patchDiscover({ tuner });
  await fetchTunerRound(tuner, false);
}

/** Record a verdict and do whatever the state machine says comes next. */
async function judgeTunerCandidate(verdict: 'keep' | 'skip'): Promise<void> {
  const tuner = appStore.state.value.discover?.tuner;
  if (tuner === undefined || tuner === null || tuner.loading) return;
  const { tuner: updated, next } = judgeCandidate(tuner, verdict);
  appStore.actions.patchDiscover({ tuner: updated });
  if (next === 'fetch-round') await fetchTunerRound(updated, true);
}

/**
 * End the session and return to the list (FR-24.7).
 *
 * Kept suggestions are merged into the list, **not** created — nothing in
 * discovery creates a topic without an explicit Add, inside the tuner or out.
 */
function finishTuner(): void {
  const current = appStore.state.value.discover;
  if (current?.tuner == null) return;
  appStore.actions.patchDiscover({
    tuner: null,
    view: 'results',
    suggestions: mergeKept(current.suggestions, current.tuner.kept),
  });
}

/**
 * Append another batch of suggestions to the list (NEWS-136).
 *
 * The names already on screen go up as `seen`, so the model is asked for ideas
 * it hasn't given yet rather than being left to repeat itself — and because the
 * cache key includes the exclusions, "More" is genuinely a new call rather than
 * a replay of the one that filled the list.
 */
async function loadMoreSuggestions(): Promise<void> {
  const current = appStore.state.value.discover;
  if (current?.source == null || current.loadingMore || current.exhausted) return;
  const seen = current.suggestions.map((s) => s.name);
  appStore.actions.patchDiscover({ loadingMore: true, error: null });
  try {
    const { suggestions } = await discoverTopics(current.source, undefined, seen);
    const latest = appStore.state.value.discover;
    // The user can close the dialog or start a different search mid-flight;
    // appending then would splice this batch onto a list it doesn't belong to.
    if (latest === null || latest.source !== current.source) return;
    const merged = mergeKept(latest.suggestions, suggestions);
    appStore.actions.patchDiscover({
      loadingMore: false,
      suggestions: merged,
      // Nothing new survived the merge, so there is no point offering again.
      exhausted: merged.length === latest.suggestions.length,
    });
  } catch (err) {
    if (appStore.state.value.discover === null) return;
    appStore.actions.patchDiscover({
      loadingMore: false,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Rename a topic, and say plainly what happened (NEWS-139).
 *
 * The dialog stays open on failure — a duplicate name is something to correct
 * in the field the user is already looking at, not a banner behind a closed
 * dialog. It closes only once the rename has actually landed.
 */
async function saveRename(
  id: string,
  name: string,
  clearItems: boolean,
  category?: { category: string | null; subcategory: string | null },
  focus?: string[],
): Promise<void> {
  try {
    // The name as it stood before this save, read now because `renameTopic`
    // refreshes state below. The Edit-topic dialog carries name, section and
    // focus (NM-27), so a save need not have touched the name at all — and
    // "Renamed to …" for a focus-only change names something that did not
    // happen (NM-29).
    const previousName = appStore.state.value.topics.find((t) => t.id === id)?.name;
    // Before the request, not after (NEWS-303, following NEWS-291's rule for the
    // app-wide clear): `renameTopic` refreshes state and feed itself, so
    // clearing afterwards leaves a frame in which the emptied feed renders with
    // the stale overlay still merged into it.
    if (clearItems) appStore.actions.clearStoryOverlaysForTopic(id);
    await renameTopic(id, name, clearItems, category, focus);
    const cleared = clearItems ? (appStore.state.value.renameItemCount ?? 0) : 0;
    appStore.actions.closeRename();
    // "Renamed to" only when the name actually changed; otherwise the edit was to
    // the section or the focus chips, and "Updated" is the honest verb (NM-29).
    const lead = previousName === undefined || previousName !== name ? `Renamed to “${name}”` : `Updated “${name}”`;
    // The count is the one the dialog already fetched when it opened (FR-25.5a),
    // so the toast names what was actually lost rather than "some stories".
    if (cleared > 0) {
      showUndoToast(`${lead} — cleared ${String(cleared)} ${cleared === 1 ? 'story' : 'stories'}`, id);
    } else {
      showToast(
        clearItems
          ? `${lead} — previous stories cleared, next check starts fresh`
          : `${lead} — applies from the next check`,
      );
    }
  } catch (err) {
    appStore.actions.setError(err instanceof Error ? err.message : String(err));
  }
}

/**
 * Restore a topic's just-cleared stories (NEWS-145).
 *
 * An expired window is **not** an error: the user pressed a button the app was
 * still showing them, and answering with the red banner reserved for real
 * failures would read as "something broke" rather than "you were too slow". It
 * replaces the toast with a plain one saying so.
 *
 * **A restored story that is still flagged does not come back into view, and
 * that is correct** (NEWS-303). The clear now drops that topic's flagged-story
 * overlay, so an undo puts the rows back in the database but not on the normal
 * feed, which excludes off-topic stories everywhere else in the app.
 *
 * The alternative — having the undo re-seed the overlay — was rejected on both
 * counts. It restores the wrong thing: this undoes the *clear*, not the *flag*,
 * and a story that is still flagged belongs where flagged stories live. Review
 * mode shows it and the sidebar badge counts it, so nothing is lost, only filed.
 * And it is not free: `POST /api/topics/:id/restore-cleared` answers with a
 * count, so the client would need the route to return the items — a server-shape
 * change to re-show rows the user has since acted on twice.
 *
 * What the overlay promises is that the row you *just* flagged stays put so a
 * misclick is reversible (NEWS-61). It has never promised to survive arbitrary
 * later actions on the topic.
 */
async function undoClear(id: string): Promise<void> {
  try {
    await restoreClearedItems(id);
    showToast('Stories restored');
  } catch (err) {
    showToast(err instanceof Error ? err.message : String(err));
  }
}

/** Create a topic from a suggestion, keeping its card in place (FR-24.12/24.13). */
async function addSuggestion(name: string): Promise<void> {
  const current = appStore.state.value.discover;
  if (current === null) return; // The dialog closed under the click; nothing to say.
  if (current.added.includes(name)) return; // Already done, and the card says so.
  const suggestion = current.suggestions.find((s) => s.name === name);
  if (suggestion === undefined) {
    // The list was replaced between the render and the click — a slower earlier
    // search landing late used to do this (NEWS-324, now guarded), and a
    // re-search still can. Silence was the bug worth fixing here: the button
    // did nothing, said nothing, and left the card looking un-added forever,
    // which is indistinguishable from a broken button.
    showToast(`“${name}” is no longer in these results — search again to add it`);
    return;
  }
  try {
    await addSuggestedTopic(suggestion);
    if (appStore.state.value.discover === null) return;
    appStore.actions.patchDiscover({ added: [...appStore.state.value.discover.added, name] });
    // `showToast`, never `setToast` directly — the store action has no timer,
    // so a direct call leaves the toast on screen forever (NEWS-141).
    showToast(`Added “${name}” — checking now`);
  } catch (err) {
    if (appStore.state.value.discover === null) return;
    appStore.actions.patchDiscover({ error: err instanceof Error ? err.message : String(err) });
  }
}

function wireEvents(root: HTMLElement): void {
  // kerf UI closed a dialog itself — Escape, its header close button, or a
  // press on the backdrop (NM-SX7NCY; a native `<dialog>` since NM-ZFF2A0).
  // The dialog is gone from the screen; clear the state that renders it so the
  // next render doesn't reopen it. A dialog the app closed (Cancel, a
  // completed action) was simply removed and fires nothing, so each closer
  // checks the state is still set.
  const dialogClosed: Record<DialogKey, () => void> = {
    confirm: () => {
      if (appStore.state.value.confirm !== null) resolveConfirm(false);
    },
    export: () => {
      appStore.actions.closeExport();
    },
    sense: () => {
      appStore.actions.closeSensePicker();
    },
    suggest: () => {
      if (appStore.state.value.topicSuggest !== null) dismissSuggest();
    },
    rename: () => {
      appStore.actions.closeRename();
    },
    capacity: () => {
      appStore.actions.closeCapacity();
    },
    checkout: () => {
      appStore.actions.closeCheckout();
    },
    settings: () => {
      if (appStore.state.value.settingsOpen) closeSettings();
    },
    discover: () => {
      appStore.actions.closeDiscover();
    },
    // Leaving the setup guide by Escape or its close button is Skip — except on
    // the last step, where closing by any means implies its primary action
    // (Start Watching / Keep Current Topics), never a silent discard (NM-245).
    onboarding: () => {
      const step = appStore.state.value.onboarding;
      if (step === null || step === 'auto') return;
      if (step === ONBOARDING_STEPS[ONBOARDING_STEPS.length - 1]) finishOnboarding();
      else closeOnboarding();
    },
    pulse: () => {
      if (appStore.state.value.pulseDetailOpen) appStore.actions.closePulse();
    },
    'add-topic': () => {
      appStore.actions.setAddTopicOpen(false);
    },
  };
  // The native `close` event does not bubble and kerf's `delegate()` only
  // captures the focus/scroll family, so this one listens on the capture phase
  // itself. It fires after kerf UI's exit motion, once the dialog has closed.
  root.addEventListener(
    'close',
    (e) => {
      const el = e.target;
      if (!(el instanceof HTMLDialogElement)) return;
      const key = el.getAttribute('data-dialog');
      if (key !== null && Object.hasOwn(dialogClosed, key)) dialogClosed[key as DialogKey]();
    },
    true,
  );

  // kerf UI layout wiring (NM-D5TYNV), once, at the stable root: the rail's
  // overlay presentation (focus, Escape, outside press) and the dividers that
  // appear between pinned chrome and content scrolled beneath it.
  wireWorkbench(root, { id: WORKBENCH_ID, panels: { leftRail: { collapsed: railCollapsed } } });
  void wireScrollDividers(root);
  wireToolbarVisibility(root);
  // Enter and Space on an interactive `ContentItem` (discovery's section tiles,
  // NM-22W019) click it, so the tile's delegated action runs from the keyboard.
  wireContentItems(root);
  // Settings → Data's Import Topics… / Import Stories… triggers (FR-30.5–30.14).
  // The wiring reports the chosen file and resets the input, so picking the
  // same file again imports again (NM-YGJFNY).
  void wireFilePickers(root, {
    onFiles: ({ id, files }) => void importPickedFile(id, files, { importTopics, importStories, showToast }),
  });
  // Live feed search (NEWS-60/76). The editor is uncontrolled — re-rendering
  // can't fight the cursor — and the search is a server query, so the refetch is
  // debounced rather than per-keystroke.
  void wireSearchFields(root, {
    collapsible: { signals: { [SEARCH_FIELD_ID]: searchExpanded } },
    onEdit: ({ id, editor }) => {
      if (id !== SEARCH_FIELD_ID) return;
      appStore.actions.setSearchQuery(readSearchField(editor).query);
      debouncedRefreshFeed();
    },
  });

  void delegate(root, 'submit', '[data-action=add-topic-form]', (e, form) => {
    e.preventDefault();
    // The kerf UI `TextField`'s native input (NM-RR1Q7G). A blank submit from
    // the fresh dialog must be a no-op (NM-ZJT0H7).
    const input = form.querySelector<HTMLInputElement>('input[name=topic-name]');
    if (!input) return;
    const name = input.value.trim();
    if (name === '') return;
    input.value = '';
    // The dialog's job is done (NM-ZJT0H7): close it before anything else, so
    // the placeholder shows in the rail and a did-you-mean dialog never stacks
    // on top of this one.
    appStore.actions.setAddTopicOpen(false);
    // Raise the "Adding…" placeholder *immediately* (NM-184), before the several-
    // second did-you-mean check runs — the add must feel instant, not wait on an AI
    // round-trip. The overlap steer (NM-148/153) + spelling correction (NM-179) then
    // run in the background; if either turns something up it opens a **dialog** (so a
    // hidden sidebar never hides it), keeping the placeholder up so the dialog's
    // choice can reconcile it. Nothing found → create straight away, reusing the
    // placeholder. On error, fall back to a plain add so the field never no-ops.
    const key = newPendingKey();
    appStore.actions.addPendingTopic({ key, name });
    void suggestSimilar(name)
      .then((resp) => {
        if (resp.suggestions.length > 0 || resp.corrections.length > 0) {
          appStore.actions.setTopicSuggest({
            name,
            suggestions: resp.suggestions,
            strongMatch: resp.strongMatch,
            corrections: resp.corrections,
            pendingKey: key,
          });
        } else {
          finishAddForPending(name, key);
        }
      })
      .catch(() => {
        finishAddForPending(name, key);
      });
  });

  // Follow a suggested existing subject (NM-148/153): subscribing to a shared
  // subject by name resolves to it, so this joins its check rather than duplicating.
  // The typed name's placeholder is dropped and a fresh one raised for the followed
  // subject (NM-184).
  void delegate(root, 'click', '[data-suggest-follow]', (_e, el) => {
    const name = el.getAttribute('data-suggest-follow');
    if (name === null) return;
    replacePendingAndAdd(name);
  });

  // Add a spelling correction (NM-179): the user typed a misspelling and picked the
  // corrected entity name, so mint a topic under *that* name — which then goes
  // through the ordinary safety + disambiguation flow like any typed name. Its
  // placeholder replaces the misspelling's (NM-184).
  void delegate(root, 'click', '[data-suggest-correct]', (_e, el) => {
    const name = el.getAttribute('data-suggest-correct');
    if (name === null) return;
    replacePendingAndAdd(name);
  });

  // Add the originally-typed name anyway (a new custom subject), dismissing the
  // steer. The placeholder already up for the typed name is *reused* — the create
  // just proceeds under it (NM-184).
  void delegate(root, 'click', '[data-suggest-add]', (_e, el) => {
    const name = el.getAttribute('data-suggest-add');
    if (name === null) return;
    const pendingKey = appStore.state.value.topicSuggest?.pendingKey ?? null;
    appStore.actions.setTopicSuggest(null);
    if (pendingKey === null) submitAddTopic(name);
    else finishAddForPending(name, pendingKey);
  });

  // Dismiss the did-you-mean dialog without adding anything (NM-184): the user
  // decided not to add, so its pending placeholder is removed too.
  void delegate(root, 'click', '[data-action=suggest-dismiss]', () => {
    dismissSuggest();
  });

  // --- Sign-in gate (NM-20, phase 3) --------------------------------------

  void delegate(root, 'click', '[data-action=sign-in]', () => {
    void doSignIn(root);
  });

  void delegate(root, 'click', '[data-action=sign-in-apple]', () => {
    void doSignIn(root, signInApple);
  });

  void delegate(root, 'click', '[data-action=register]', () => {
    void completeRegistration();
  });

  void delegate(root, 'click', '[data-action=sign-out]', () => {
    void doSignOut();
  });

  // --- Topic discovery (NEWS-126) -----------------------------------------

  void delegate(root, 'click', '[data-action=open-discover]', () => {
    appStore.actions.openDiscover();
  });

  // Closing — the header ✕, Escape, or a press on the backdrop — is kerf
  // UI's, and lands in `dialogClosed.discover` above (NM-22W019).

  void delegate(root, 'submit', '[data-action=discover-search]', (e, form) => {
    e.preventDefault();
    // One search at a time (NEWS-324). The submit button is disabled while one
    // runs, which already blocks a native Enter submit; the guard here keeps
    // the rule independent of the button.
    if (appStore.state.value.discover?.loading === true) return;
    // The kerf UI `TextField`'s native input (NM-RR1Q7G).
    const input = form.querySelector<HTMLInputElement>('input[name=discover-query]');
    // An empty query is a real request — "surprise me" (FR-24.3) — so this
    // deliberately does not bail out on a blank field the way add-topic does.
    void runDiscovery({ kind: 'describe', query: (input?.value ?? '').trim() });
  });

  /**
   * One delegate for both browse steps, on one action — deliberately not two.
   *
   * The section tiles (interactive `ContentItem`s) and the subcategory chips
   * (action `Chip`s) both carry `data-action="discover-nav"` with the target in
   * `data-item-id`. When they were both `<button>`s in the same position of the
   * same container, the morph **reused the node** and merely rewrote its
   * attributes when the pane switched. Two delegates keyed on two
   * different attributes then both match the *same* click: the first handler
   * re-renders synchronously, the tile becomes a chip, and the second handler —
   * still walking up from that very node — sees the chip's attribute and fires
   * too. One physical click ran two different actions, jumping the user from
   * "Sports" straight into results for whichever subcategory landed under the
   * cursor. Caught by the E2E suite; see `docs/3-ui.md`.
   */
  void delegate(root, 'click', '[data-action=discover-nav]', (e, el) => {
    const value = el.getAttribute('data-item-id');
    if (value === null) return;
    // A nav click triggers a synchronous re-render (patchDiscover / runDiscovery),
    // and kerf **reuses DOM nodes** across the pane switch. This same click is
    // still bubbling, so without stopping it the later-registered `discover-back`
    // listener re-reads the *morphed* DOM: the reused chip node has become the
    // results/waiting pane's Back button, `closest('[data-action=discover-back]')`
    // matches it, and Back fires — instantly undoing the forward navigation. That
    // is the "first pill in each category does nothing" bug (NM-243): only the
    // first chip sits where the morph turns it into the Back control. Stop the
    // click the moment we commit to a morph so no other delegate on `root` acts on
    // this event after the DOM has changed underneath it. (Sibling to the
    // one-delegate merge above, which closed the section→sub form of the same
    // node-reuse hazard.)
    if (value.startsWith('section:')) {
      e.stopImmediatePropagation();
      appStore.actions.patchDiscover({ section: value.slice('section:'.length), view: 'browse' });
      return;
    }
    if (!value.startsWith('sub:')) return;
    e.stopImmediatePropagation();
    // "sub:category/subcategory", with an empty tail meaning the whole section.
    const parts = value.slice('sub:'.length).split('/');
    const category = parts.at(0);
    const sub = parts.at(1);
    if (category === undefined || sub === undefined) return;
    void runDiscovery({ kind: 'section', category, subcategory: sub === '' ? null : sub });
  });

  void delegate(root, 'click', '[data-action=discover-back]', (e) => {
    const current = appStore.state.value.discover;
    if (current === null) return;
    // Same node-reuse hazard as `discover-nav` above, in the other direction: Back
    // morphs the pane too, so stop the click before a reused node can carry it to a
    // later delegate (discover-more / discover-retry).
    e.stopImmediatePropagation();
    // Back must work while a search is in flight (NM-117): clear `loading` so the
    // body stops rendering the waiting pane, and cancel the request so its late
    // response can't yank the user back to results after they left.
    if (current.loading) cancelDiscoverInFlight();
    // From results, step back to wherever they came from; from a section's
    // subcategories, back to the grid.
    if (current.view === 'results' && current.source?.kind === 'section') {
      appStore.actions.patchDiscover({ view: 'browse', section: current.source.category, error: null, loading: false });
      return;
    }
    appStore.actions.patchDiscover({ view: 'browse', section: null, error: null, loading: false });
  });

  void delegate(root, 'click', '[data-action=discover-more]', () => {
    void loadMoreSuggestions();
  });

  void delegate(root, 'click', '[data-action=discover-retry]', () => {
    const source = appStore.state.value.discover?.source;
    if (source) void runDiscovery(source);
    else appStore.actions.patchDiscover({ error: null, view: 'browse', section: null });
  });

  /**
   * Enter the tuner from a card or from the whole set (FR-24.5).
   *
   * One attribute, one delegate — see the delegate/morph rule in `docs/3-ui.md`.
   */
  void delegate(root, 'click', '[data-tune]', (_e, el) => {
    const value = el.getAttribute('data-tune');
    if (value === null) return;
    const separator = value.indexOf(':');
    const direction = value.slice(0, separator);
    const anchor = value.slice(separator + 1);
    if (direction !== 'narrower' && direction !== 'similar') return;
    void enterTuner(anchor, direction);
  });

  /** Keep / skip / done, all on one attribute for the same reason. */
  void delegate(root, 'click', '[data-tuner]', (_e, el) => {
    const action = el.getAttribute('data-tuner');
    if (action === 'done') {
      finishTuner();
      return;
    }
    if (action === 'keep' || action === 'skip') void judgeTunerCandidate(action);
  });

  void delegate(root, 'click', '[data-add-suggestion]', (_e, el) => {
    const name = el.getAttribute('data-add-suggestion');
    if (name !== null) void addSuggestion(name);
  });

  // The empty feed's Popular Topics chips, and discovery's profile and
  // evergreen strips (NEWS-406, NM-30): kerf UI action `Chip`s, which own
  // `data-action` and carry the name as `data-item-id`. Plain `addTopic`, not
  // `addSuggestion`: these are names from a static table with no reason, kind
  // or classification behind them, so there is no suggestion to mark as added —
  // a discovery chip disappears on the next render because the topic now
  // excludes itself (FR-24.11).
  void delegate(root, 'click', '[data-action=add-starter-topic]', (_e, el) => {
    const name = el.getAttribute('data-item-id');
    if (name !== null) void addTopic(name);
  });

  // The rail's sort: the "Watching" header's action opens a checked-item
  // context `PopupMenu` (NM-WXNQ4F).
  void delegate(root, 'click', '[data-action=open-topic-sort]', (_e, el) => {
    openTopicSortMenu(el);
  });
  void delegate(root, 'click', '[data-action=topic-sort]', (_e, el) => {
    const value = el.getAttribute('data-sort') ?? '';
    if (TOPIC_SORTS.includes(value as (typeof TOPIC_SORTS)[number])) {
      appStore.actions.setTopicSort(value as (typeof TOPIC_SORTS)[number]);
    }
  });

  // The rail's one show/hide toggle, in the app bar (NM-614VD8).
  void delegate(root, 'click', '[data-action=toggle-sidebar]', () => {
    setRailCollapsed(!railCollapsed.value);
  });
  // "Add Topic…" in the feed toolbar and the empty state's "Add a Topic" open
  // the Add Topic dialog (NM-ZJT0H7); its field autofocuses, which on a phone
  // raises the keyboard. Cancel closes it; Escape and the header close are kerf
  // UI's and land in `dialogClosed['add-topic']`.
  void delegate(root, 'click', '[data-action=open-add-topic]', () => {
    appStore.actions.setAddTopicOpen(true);
    focusAddTopicField(root);
  });
  void delegate(root, 'click', '[data-action=close-add-topic]', () => {
    appStore.actions.setAddTopicOpen(false);
  });

  // The mobile "Watching · N" summary bar's `expand-watching` action was removed
  // with the bar itself (NM-235) — the header toggle is the only opener now.

  void delegate(root, 'click', '[data-action=rerun-onboarding]', () => {
    appStore.actions.setSettingsOpen(false);
    appStore.actions.setOnboarding('welcome');
  });

  void delegate(root, 'click', '[data-action=open-settings]', (_e, el) => {
    // Remember what opened it, so focus returns there when it closes (NM-94).
    settingsOpener = el instanceof HTMLElement ? el : null;
    // Always reopen on the first tab: wherever you left off is rarely where you
    // want to be next time, and a dialog that remembers is a dialog that opens
    // somewhere surprising. The first tab is App now (NM-38) — the everyday
    // settings.
    appStore.actions.setSettingsTab('app');
    appStore.actions.setSettingsOpen(true);
  });

  // Settings' sections (a kerf UI `SegmentedControl`: each segment is a native
  // button carrying `data-segment-value`, in the normal Tab order).
  void delegate(root, 'click', '[data-action=settings-tab]', (_e, el) => {
    const tab = el.getAttribute('data-segment-value');
    if (tab === 'app' || tab === 'data') appStore.actions.setSettingsTab(tab);
  });

  // Budget is committed on `change` (blur / Enter), not `input` — a PATCH per
  // keystroke would round-trip "1", "12", "125" and fight the 4 s state poll
  // for the field. Blank means no limit.

  // `change`, not `input`, to avoid a PATCH per keystroke — and here it
  // also matters for IME input: a composing keystroke in a CJK script is not a
  // finished word, and `input` would PATCH each intermediate candidate.
  void delegate(root, 'change', 'input[name=location]', (_e, el) => {
    if (el instanceof HTMLInputElement) void updateLocation(el.value.trim());
  });

  void delegate(root, 'click', '[data-action=clear-stories]', () => {
    void (async () => {
      const total = appStore.state.value.feedTotal;
      const running = appStore.state.value.checking.length;
      // Named, not "are you sure?" — and it says what *survives*, because the
      // fear this dialog has to answer is "am I about to lose my topics too".
      //
      // The running-check sentence appears only when one is running (NEWS-271).
      // Clearing now stops them rather than refusing, and stopping a check the
      // user is waiting on is a consequence they should hear about *before* they
      // agree, not afterwards in a toast.
      const ok = await confirm(
        `Delete ${total > 0 ? String(total) : 'all'} stor${total === 1 ? 'y' : 'ies'} from every topic? ` +
          (running > 0
            ? `${running === 1 ? 'The check' : `All ${String(running)} checks`} running now will be stopped. `
            : '') +
          `Your topics and settings are not touched. This cannot be undone.`,
        { confirmLabel: 'Delete Stories', danger: true, title: 'Delete All Stories?', holdSeconds: 3 },
      );
      if (!ok) return;
      try {
        // Before the request, so there is no window in which the refreshed
        // (empty) feed renders with the stale overlay still merged into it.
        appStore.actions.clearStoryOverlays();
        const { cleared, cancelledChecks } = await clearAllStories();
        showToast(
          `Deleted ${String(cleared)} stor${cleared === 1 ? 'y' : 'ies'}` +
            (cancelledChecks > 0
              ? `, stopped ${String(cancelledChecks)} check${cancelledChecks === 1 ? '' : 's'}`
              : ''),
        );
      } catch (err) {
        showToast(`Delete failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    })();
  });

  /**
   * Delete every topic (FR-31.1, NEWS-328).
   *
   * The confirm names the count and says what **survives**, like its neighbour —
   * "delete all topics" is exactly the phrase that raises the fear it means the
   * whole app, and the answer is that settings and keys are untouched.
   *
   * It also names the stories going with them, which is the part someone might
   * not have thought through: a topic owns its stories, so this is strictly more
   * destructive than the button beside it.
   */
  void delegate(root, 'click', '[data-action=clear-topics]', () => {
    void (async () => {
      const { topics, feedTotal, checking } = appStore.state.value;
      const running = checking.length;
      const ok = await confirm(
        `Delete ${topics.length > 0 ? String(topics.length) : 'all'} topic${topics.length === 1 ? '' : 's'}? ` +
          `Every story filed under ${topics.length === 1 ? 'it' : 'them'} goes too` +
          (feedTotal > 0 ? ` — ${String(feedTotal)} of them` : '') +
          '. ' +
          (running > 0
            ? `${running === 1 ? 'The check' : `All ${String(running)} checks`} running now will be stopped. `
            : '') +
          'Your settings are not touched. This cannot be undone.',
        { confirmLabel: 'Delete Topics', danger: true, title: 'Delete All Topics?', holdSeconds: 3 },
      );
      if (!ok) return;
      try {
        // Before the request, for the same reason the story clear does it: no
        // window in which a refreshed, empty rail renders with a stale overlay.
        appStore.actions.clearStoryOverlays();
        const { deleted, cancelledChecks } = await deleteAllTopics();
        showToast(
          `Deleted ${String(deleted)} topic${deleted === 1 ? '' : 's'}` +
            (cancelledChecks > 0
              ? `, stopped ${String(cancelledChecks)} check${cancelledChecks === 1 ? '' : 's'}`
              : ''),
        );
      } catch (err) {
        showToast(`Delete failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    })();
  });

  // The `recover-db` handler (the in-app set-aside recovery, doc 33) was removed
  // in NM-171: a quarantined database is restored at the terminal now, the step
  // the startup log prints. The FR-4.17 quarantine banner + its dismiss stay.

  /**
   * Theme (FR-3.75, NEWS-334, NM-25).
   *
   * **The store is the only writer**, and the handler deliberately does not
   * apply the theme itself. Writing it here optimistically *and* from the
   * `settings.theme` effect below means two writers disagreeing for as long as
   * the PATCH is in flight: any unrelated store change in that window — a poll,
   * a check starting — re-runs the effect with the old value and puts the
   * previous palette back. Measured: pinning dark reverted to light before the
   * request landed.
   *
   * `updateTheme` refreshes state as soon as the PATCH returns, and this server
   * is on loopback, so the round trip costs a few milliseconds rather than a
   * visible beat.
   *
   * NM-25 swapped the `<select>` for a group of radio tiles, so the chosen
   * value comes off the input's `data-theme` (read with `getAttribute` rather
   * than a cast — the element is now an `<input>`, not a `<select>`). The
   * selected tile keeps itself right across a morph via `syncRadios` below, the
   * radio twin of the long-standing `<select>` fix.
   */
  void delegate(root, 'change', '[data-action=theme]', (_e, el) => {
    const value = el.getAttribute('data-theme');
    if (value !== 'auto' && value !== 'light' && value !== 'dark') return;
    void updateTheme(value);
  });

  // --- first-run flow (NEWS-78) ---------------------------------------------

  void delegate(root, 'click', '[data-undo-clear]', (_e, el) => {
    const id = el.getAttribute('data-undo-clear');
    if (id !== null) void undoClear(id);
  });

  // The setup guide's starters and interests are kerf UI `ChoiceChips`
  // (NM-QJFW31): native checkboxes, so a toggle is a `change` naming its value.
  // An already-watched starter is a disabled choice and never fires.
  void delegate(root, 'change', 'input[data-action=starter-topic]', (_e, el) => {
    if (el instanceof HTMLInputElement) appStore.actions.toggleOnboardingTopic(el.value);
  });

  void delegate(root, 'change', 'input[data-action=profile]', (_e, el) => {
    if (el instanceof HTMLInputElement) appStore.actions.toggleOnboardingProfile(el.value);
  });

  // Skips the remaining profile *pages*, not the wizard. Saves what is already
  // ticked on the way out — a user who picked six things on page one and then
  // skipped meant to keep the six.
  void delegate(root, 'click', '[data-action=profiles-skip]', () => {
    void saveProfilesAndAdvance();
  });

  void delegate(root, 'change', 'input[name=onboarding-location]', (_e, el) => {
    if (el instanceof HTMLInputElement) void updateLocation(el.value.trim());
  });

  // The continents are a kerf UI `ChoiceChips` radio group. `click`, not
  // `change`: a press on the chosen continent clears it — a radio fires no
  // `change` for that — so the row is a toggle rather than a one-way door.
  // Arrow keys select a radio with a synthetic click, so they land here too.
  void delegate(root, 'click', 'input[data-action=location-pick]', (_e, el) => {
    if (!(el instanceof HTMLInputElement)) return;
    void updateLocation(appStore.state.value.settings.location === el.value ? '' : el.value);
  });

  void delegate(root, 'click', '[data-action=onboarding-skip]', () => {
    closeOnboarding();
  });

  void delegate(root, 'click', '[data-action=onboarding-next]', () => {
    const current = appStore.state.value.onboarding;
    if (current === null || current === 'auto') return;
    // The profile picker holds three pages behind one step, so Continue pages
    // through them before it advances the wizard (NEWS-383).
    if (current === 'profiles') {
      const { page, advanceStep } = nextProfilePage(appStore.state.value.onboardingProfilePage, PROFILE_PAGE_COUNT);
      if (!advanceStep) {
        appStore.actions.setOnboardingProfilePage(page);
        return;
      }
      void saveProfilesAndAdvance();
      return;
    }
    // Past the end means "done".
    const next = ONBOARDING_STEPS.at(ONBOARDING_STEPS.indexOf(current) + 1);
    if (next === undefined) {
      finishOnboarding();
      return;
    }
    advanceOnboardingTo(next);
  });

  // Back a step (NM-246). The left foot control is a Back button on every step
  // but the first; from the last step it replaces the old "Close" (NM-245).
  void delegate(root, 'click', '[data-action=onboarding-back]', () => {
    goBackOneOnboardingStep();
  });

  // A step dot jumps back to an already-seen step (NM-246). The forward/current
  // dots are rendered `disabled`, so a click only ever lands on an earlier step;
  // the guard here makes that a property of the handler too, not just the markup.
  void delegate(root, 'click', '[data-onboarding-step]', (_e, el) => {
    const target = el.getAttribute('data-onboarding-step');
    const current = appStore.state.value.onboarding;
    if (target === null || current === null || current === 'auto') return;
    if (!isOnboardingStep(target)) return;
    if (ONBOARDING_STEPS.indexOf(target) >= ONBOARDING_STEPS.indexOf(current)) return;
    advanceOnboardingTo(target);
  });

  // --- topic edit: name, section, focus (NEWS-139, NEWS-407, NM-27) --------

  void delegate(root, 'submit', '[data-save-rename]', (e, form) => {
    e.preventDefault();
    const id = form.getAttribute('data-save-rename');
    // Native controls inside kerf UI's `TextField` and `Checkbox` (NM-RR1Q7G).
    const field = form.querySelector<HTMLInputElement>('input[name=topic-name]');
    const clearBox = form.querySelector<HTMLInputElement>('input[name=clear-items]');
    if (id === null || !field) return;
    const name = field.value.trim();
    if (name === '') return;
    const catSel = form.querySelector<HTMLSelectElement>('[name=topic-category]');
    const subSel = form.querySelector<HTMLSelectElement>('[name=topic-subcategory]');
    // Absent select means the dialog predates this field; `''` means "let
    // Newsmonger decide", which is a real instruction (clear it *and* make the
    // topic eligible for automatic classification again) and not the same as
    // "leave it alone" (FR-22.7).
    const category =
      catSel === null
        ? undefined
        : {
            category: catSel.value === '' ? null : catSel.value,
            subcategory: catSel.value === '' || !subSel || subSel.value === '' ? null : subSel.value,
          };
    // The ticked focus chips (NM-27). Parsed at the boundary rather than trusted
    // raw — the values come from our own FOCUS_OPTIONS, and the server also
    // rejects an unknown id. An absent fieldset (an older dialog) leaves it
    // undefined so the PATCH omits `focus` rather than clearing it.
    const focusInputs = form.querySelectorAll<HTMLInputElement>('input[name=focus]');
    const focus =
      focusInputs.length === 0
        ? undefined
        : FocusIdsSchema.parse([...focusInputs].filter((el) => el.checked).map((el) => el.value));
    void saveRename(id, name, clearBox?.checked === true, category, focus);
  });

  // Re-render only the subcategory container, not the form: the section decides
  // which subjects exist, and re-rendering the whole dialog would discard a
  // half-typed name (NEWS-407).
  void delegate(root, 'change', 'kui-select[name=topic-category]', (_e, el) => {
    const value = (el as HTMLSelectElement).value;
    appStore.actions.setRenameCategory(value === '' ? null : value);
  });

  void delegate(root, 'change', 'kui-select[name=topic-subcategory]', (_e, el) => {
    const value = (el as HTMLSelectElement).value;
    appStore.actions.setRenameSubcategory(value === '' ? null : value);
  });

  void delegate(root, 'click', '[data-action=close-rename]', () => {
    appStore.actions.closeRename();
  });

  // --- disambiguation sense picker (NM-163) --------------------------------

  void delegate(root, 'click', '[data-action=pick-sense]', (_e, el) => {
    const topicId = el.getAttribute('data-sense-topic');
    const indexAttr = el.getAttribute('data-sense-index');
    if (topicId === null || indexAttr === null) return;
    const topic = appStore.state.value.topics.find((t) => t.id === topicId);
    // Read the sense text off the topic by index rather than an attribute, so no
    // sense phrase has to survive HTML-attribute escaping.
    const sense = topic?.senseOptions[Number.parseInt(indexAttr, 10)];
    if (topic === undefined || sense === undefined) return;
    appStore.actions.closeSensePicker();
    void chooseTopicSense(topicId, sense).then(() => {
      showToast(`Following “${topic.name}” — ${sense}`);
    });
  });

  void delegate(root, 'click', '[data-action=close-sense]', () => {
    appStore.actions.closeSensePicker();
  });

  // Backdrop click on the did-you-mean dialog dismisses it (NM-184) — same as
  // Cancel, so the pending placeholder is removed too.

  // --- capacity / disabled group (NM-201) ----------------------------------

  void delegate(root, 'click', '[data-action=toggle-disabled-group]', () => {
    appStore.actions.toggleDisabledGroup();
  });
  void delegate(root, 'click', '[data-action=open-enable-capacity]', () => {
    appStore.actions.openEnableCapacity();
  });
  void delegate(root, 'click', '[data-capacity-pick]', (_e, el) => {
    const id = el.getAttribute('data-capacity-pick');
    if (id !== null) appStore.actions.toggleCapacityPick(id);
  });
  void delegate(root, 'click', '[data-action=capacity-select-all]', () => {
    selectAllCapacity();
  });
  void delegate(root, 'click', '[data-action=capacity-select-none]', () => {
    appStore.actions.setCapacityPicked([]);
  });
  void delegate(root, 'click', '[data-action=capacity-submit]', () => {
    void commitCapacity();
  });
  void delegate(root, 'click', '[data-action=close-capacity]', () => {
    appStore.actions.closeCapacity();
  });

  // --- upgrade checkout (NM-225/226) ---------------------------------------

  // Open from the sidebar button (NM-226) or the Settings account group (NM-225).
  // Pre-select the cheapest tier above the current plan so the form is one step
  // shorter for the common "next tier up" case.
  void delegate(root, 'click', '[data-action=open-checkout]', () => {
    const s = appStore.state.value;
    if (!s.billing.enabled || s.plan === null) return;
    const current = s.billing.plans.find((p) => p.id === s.plan?.id);
    const currentPrice = current?.priceUsd ?? 0;
    const nextUp = s.billing.plans.filter((p) => p.priceUsd > currentPrice).at(0) ?? null;
    appStore.actions.openCheckout(nextUp?.id ?? null);
  });
  void delegate(root, 'click', '[data-checkout-plan]', (_e, el) => {
    const plan = el.getAttribute('data-checkout-plan');
    if (plan !== null) appStore.actions.updateCheckout({ plan, error: null });
  });
  void delegate(root, 'click', '[data-action=submit-checkout]', (_e, el) => {
    const s = appStore.state.value;
    if (s.checkout === null || s.checkout.plan === null || s.checkout.submitting) return;
    // Read the card straight off the input — an uncontrolled field, so no
    // per-keystroke store write (and the number never lingers in app state).
    const input = el.closest('dialog[data-dialog=checkout]')?.querySelector<HTMLInputElement>('[name=checkout-card]');
    void submitUpgrade(s.checkout.plan, input?.value ?? '');
  });
  void delegate(root, 'click', '[data-action=close-checkout]', () => {
    appStore.actions.closeCheckout();
  });

  // --- topic selection -----------------------------------------------------

  // A topic row's primary button (kerf UI `ListActionRow`, NM-YGX7HY). Matched
  // on the button, not the row root, so a press on the row's own "…" menu
  // trigger never selects the row underneath it.
  void delegate(root, 'click', '[data-topic-row] [data-action=select-topic]', (e, el) => {
    const id = el.closest('[data-topic-row]')?.getAttribute('data-topic-row') ?? null;
    if (id === null || !(e instanceof MouseEvent)) return;
    // A topic awaiting a sense (NM-163) opens its picker instead of selecting —
    // the whole "needs attention" row is the affordance.
    const rowTopic = appStore.state.value.topics.find((t) => t.id === id);
    if (rowTopic !== undefined && isAwaitingSense(rowTopic)) {
      appStore.actions.openSensePicker(id);
      return;
    }
    // Cmd on macOS, Ctrl elsewhere — reading both is simpler and more forgiving
    // than sniffing the platform, and no OS uses them for conflicting meanings.
    const toggle = e.metaKey || e.ctrlKey;
    const range = e.shiftKey;
    selectTopic(id, { toggle, range });
    if (!toggle && !range && e.detail <= 1) {
      const prior = appStore.state.value.soloTopicIds;
      const next = transferSingleSolo(prior, id);
      clickSoloTransfer = next.length === 1 && next[0] === id && prior[0] !== id ? { id, prior: [...prior] } : null;
      if (next[0] !== prior[0]) {
        appStore.actions.setSolo(next);
        void refreshFeed();
        void refreshPulseSurfaces();
      }
    }
  });

  // --- section filter bar (NEWS-97) ----------------------------------------

  // The section filter (kerf UI `ChoiceChips`: native radios, so a choice is a
  // `change`, by pointer or arrow key alike).
  void delegate(root, 'change', '[data-action=filter-category]', (_e, el) => {
    if (appStore.state.value.soloTopicIds.length > 0) return;
    const value = (el as HTMLInputElement).value;
    // Selecting a section always resets the sub-row: the previous subsection
    // belongs to a different parent and would match nothing.
    appStore.actions.setCategoryFilter(value === ALL_FILTER ? null : { category: value, subcategory: null });
    void refreshFeed();
    void refreshPulseSurfaces();
  });

  void delegate(root, 'change', '[data-action=filter-subcategory]', (_e, el) => {
    if (appStore.state.value.soloTopicIds.length > 0) return;
    const current = appStore.state.value.categoryFilter;
    if (current === null) return;
    const value = (el as HTMLInputElement).value;
    appStore.actions.setCategoryFilter({
      category: current.category,
      subcategory: value === ALL_FILTER ? null : value,
    });
    void refreshFeed();
    void refreshPulseSurfaces();
  });

  // Double-click toggles solo (NEWS-95) — the one topic action common enough to
  // deserve a gesture instead of a trip through the right-click menu.
  //
  // Acts on the double-clicked row alone, never the wider selection: the two
  // clicks that make up the gesture have already collapsed the selection to
  // this row (a plain click sets it), so anything else would act on rows the
  // user can no longer see are targeted.
  //
  // Routed through `runTopicAction` rather than reimplemented, so the gesture
  // and the menu item can't drift apart — including the additive behaviour,
  // where soloing a second topic widens the filter instead of replacing it.
  // `.topic` is `user-select: none`, so this can't leave a stray text selection.
  void delegate(root, 'dblclick', '[data-topic-row] [data-action=select-topic]', (e, el) => {
    const id = el.closest('[data-topic-row]')?.getAttribute('data-topic-row') ?? null;
    if (id === null || !(e instanceof MouseEvent)) return;
    const transferred = clickSoloTransfer?.id === id ? clickSoloTransfer : null;
    clickSoloTransfer = null;
    if (transferred === null) {
      runTopicAction('solo', [id]);
    } else {
      appStore.actions.setSolo(toggleSolo(transferred.prior, [id]));
      void refreshFeed();
      void refreshPulseSurfaces();
    }
  });

  // Keyboard equivalents for the row (NEWS-90). The context menu is the only
  // route to check / pause / solo / rename / delete, so without
  // these the whole topic action set is mouse-only.
  void delegate(root, 'keydown', '[data-topic-row] [data-action=select-topic]', (e, el) => {
    const row = el.closest('[data-topic-row]');
    const id = row?.getAttribute('data-topic-row') ?? null;
    if (row === null || id === null || !(e instanceof KeyboardEvent)) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // Activating a "needs attention" row opens its sense picker (NM-163),
      // matching the click gesture, rather than selecting it.
      const rowTopic = appStore.state.value.topics.find((t) => t.id === id);
      if (rowTopic !== undefined && isAwaitingSense(rowTopic)) {
        appStore.actions.openSensePicker(id);
        return;
      }
      selectTopic(id, { toggle: e.metaKey || e.ctrlKey, range: e.shiftKey });
      return;
    }
    // The platform gesture for "open this element's menu": macOS has no Menu
    // key, so Shift+F10 is the one that has to work everywhere.
    if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
      e.preventDefault();
      const box = el.getBoundingClientRect();
      openTopicMenuAt(row, id, box.left + 24, box.bottom - 4);
    }
  });

  // Right-click opens the row's own action menu at the pointer (kerf UI: a
  // row's visible overflow menu doubles as its context menu, NM-YGX7HY).
  void delegate(root, 'contextmenu', '[data-topic-row]', (e, el) => {
    const id = el.getAttribute('data-topic-row');
    if (id === null || !(e instanceof MouseEvent)) return;
    e.preventDefault();
    openTopicMenuAt(el, id, e.clientX, e.clientY);
  });

  // A topic menu command. It acts on the whole selection when its row is part
  // of a multi-row selection, else on its own row — the same rule the menu's
  // labels were rendered with (`topicMenuSpecFor`).
  void delegate(root, 'click', '[data-menu-action]', (_e, el) => {
    const action = el.getAttribute('data-menu-action');
    const id = el.getAttribute('data-topic-id');
    if (action === null || id === null) return;
    const menu = el.closest<PopupMenuElement>('[data-component="popup-menu"]');
    if (menu !== null) closePopupMenu(menu);
    const current = appStore.state.value.selectedTopicIds;
    runTopicAction(action, current.includes(id) && current.length > 1 ? current : [id]);
  });

  // --- Story menu (bookmark / share / flag), NEWS-61 ---
  // Right-click opens the card's own "…" menu at the pointer (kerf UI: a
  // visible overflow menu doubles as the context menu, NM-MACR4Q).
  void delegate(root, 'contextmenu', '[data-story-id]', (e, el) => {
    if (!(e instanceof MouseEvent)) return;
    const menu = el.querySelector<PopupMenuElement>('[data-component="popup-menu"]');
    if (menu === null) return;
    e.preventDefault();
    openPopupMenuAt(menu, e.clientX, e.clientY);
  });
  void delegate(root, 'click', '[data-item-menu-action]', (_e, el) => {
    const action = el.getAttribute('data-item-menu-action');
    const id = el.getAttribute('data-story-ref');
    if (action === null || id === null) return;
    const menu = el.closest<PopupMenuElement>('[data-component="popup-menu"]');
    if (menu !== null) closePopupMenu(menu);
    const item = feedAndFlagged().find((i) => i.id === id);
    if (item === undefined) return;
    if (action === 'bookmark') void setItemSaved(item.id, !item.saved);
    else if (action === 'share') void shareOne(item);
    else if (action === 'flag') flagItem(item, !item.offTopic);
  });
  // The "Off Topic" chip on a collapsed row prompts to unflag (a kerf UI action
  // `Chip`, which carries the story id as `data-item-id`).
  void delegate(root, 'click', '[data-action=unflag-story]', (_e, el) => {
    const id = el.getAttribute('data-item-id');
    if (id === null) return;
    void (async () => {
      if (await confirm('Unflag this story? It will return to the feed.', { confirmLabel: 'Unflag' })) {
        appStore.actions.removeRecentlyFlagged(id);
        await setItemOffTopic(id, false);
        await refreshFeed();
      }
    })();
  });
  void delegate(root, 'click', '[data-action=exit-review]', () => {
    appStore.actions.setReviewTopicIds([]);
    void refreshFeed();
  });

  void delegate(root, 'click', '[data-action=clear-solo]', () => {
    appStore.actions.setSolo([]);
    void refreshFeed();
    void refreshPulseSurfaces();
  });
  void delegate(root, 'click', '[data-action=exit-solo-pulse]', () => {
    appStore.actions.setSolo([]);
    void refreshFeed();
    void refreshPulseSurfaces();
  });

  // --- Topic/category pulse (NEWS-453) ------------------------------------

  void delegate(root, 'click', '[data-open-pulse-kind]', (_e, el) => {
    const kind = el.getAttribute('data-open-pulse-kind');
    const id = el.getAttribute('data-open-pulse-id');
    if ((kind !== 'topic' && kind !== 'category') || id === null) return;
    const subcategory = el.getAttribute('data-open-pulse-subcategory');
    void loadPulseDetail({ kind, id, subcategory: subcategory === '' ? null : subcategory }, 30);
  });
  // The drill-down closes through kerf UI (Escape, its close button, a press
  // on the backdrop) and the dialog `close` listener above (NM-NWFSDV). Its
  // period is a kerf UI `SegmentedControl`: the value is in `data-segment-value`.
  void delegate(root, 'click', '[data-action=pulse-days]', (_e, el) => {
    const raw = Number(el.getAttribute('data-segment-value'));
    if (raw !== 7 && raw !== 30 && raw !== 90) return;
    // The scope is the one being loaded, else the one on screen.
    const { pulsePending, pulseDetail } = appStore.state.value;
    const scope = pulsePending ?? pulseDetail?.scope ?? null;
    if (scope === null) return;
    void loadPulseDetail({ kind: scope.kind, id: scope.id, subcategory: scope.subcategory }, raw);
  });

  // --- Expandable story card (NEWS-281) ---
  //
  // ONE delegate for the whole gesture, the expander button included, per the
  // NEWS-126 lesson: `delegate()` runs every matching handler for the same
  // click, and the first one to re-render moves the DOM under the ones that
  // follow. A second handler on the button would have to guess whether this one
  // had already fired.
  //
  // Left-click only — `contextmenu` still opens the story menu, untouched.
  void delegate(root, 'click', '[data-story-id]', (e, el) => {
    const id = el.getAttribute('data-story-id');
    if (id === null || !(e.target instanceof Element)) return;
    // The expander's presence *is* the affordance, so ask the DOM rather than
    // re-deriving "is this expandable" from the variant: a flagged one-liner and
    // a review-mode card both render without one, and both must stay inert.
    if (el.querySelector('[data-expand-item]') === null) return;
    const target = e.target;
    if (target.closest('[data-expand-item]') === null) {
      // Everything else that already owns a click inside the card. Without these
      // the toggle rides along with the action the user actually pressed — most
      // visibly on a source link, which would open a tab AND move the card.
      if (target.closest('button, a, [data-component="popup-menu"]') !== null) return;
      if (target.closest('[data-sources]') !== null) return;
      // Content the pane grows (NEWS-282) is its own; clicking into it must not
      // close the thing it is inside.
      if (target.closest('[data-story-pane]') !== null) return;
      // Selecting the summary ends in a click on the card. Collapsing the story
      // someone is copying out of would be the worst possible reading of that.
      const selection = window.getSelection();
      if (selection !== null && !selection.isCollapsed) return;
    }
    appStore.actions.toggleItemExpanded(id);
    // Fetch the thread **on expand only** (NEWS-282), and only when the feed's
    // thread summary says there is one — a thread of one needs no request, which
    // is the majority of cards. `loadThread` also owns the cache, so a
    // collapse/re-expand costs nothing.
    if (appStore.state.value.expandedItemId === id) void loadThread(id);
  });

  // Retry a thread whose fetch failed (NEWS-282). A separate delegate is safe
  // here — unlike the NEWS-126 case — because the expand handler above returns
  // early for anything inside `.item-pane`, so exactly one of the two acts.
  void delegate(root, 'click', '[data-retry-thread]', (_e, el) => {
    const id = el.getAttribute('data-retry-thread');
    if (id === null) return;
    void loadThread(id);
  });

  void delegate(root, 'click', '[data-action=show-all-thread]', () => {
    appStore.actions.showAllThread();
  });

  void delegate(root, 'click', '[data-save-item]', (_e, el) => {
    const id = el.getAttribute('data-save-item');
    if (id === null) return;
    const saved = el.getAttribute('data-saved') === 'true';
    void setItemSaved(id, !saved);
  });
  void delegate(root, 'click', '[data-share-item]', (_e, el) => {
    const id = el.getAttribute('data-share-item');
    if (id === null) return;
    const item = feedAndFlagged().find((i) => i.id === id);
    if (item !== undefined) void shareOne(item);
  });
  void delegate(root, 'click', '[data-action=toggle-saved-filter]', () => {
    appStore.actions.setSavedFilter(!appStore.state.value.savedFilter);
    void refreshFeed();
  });
  void delegate(root, 'click', '[data-action=clear-saved-filter]', () => {
    appStore.actions.setSavedFilter(false);
    void refreshFeed();
  });

  void delegate(root, 'click', '[data-action=show-more]', () => {
    appStore.actions.showMoreFeed();
    void refreshFeed();
  });

  // The search field's clear button (kerf UI `SearchField`): the editor's
  // text is DOM-owned, so clearing the store is not enough — bump the revision so
  // the field rebuilds its editor from the now-empty query.
  void delegate(root, 'click', '[data-action=clear-search]', () => {
    debouncedRefreshFeed.cancel();
    appStore.actions.setSearchQuery('');
    searchRevision.value += 1;
    void refreshFeed();
  });

  void delegate(root, 'click', '[data-action=dismiss-error]', () => {
    appStore.actions.setError(null);
  });
  // Server-owned, unlike every other banner here: dismissing deletes the row,
  // so the notice cannot come back on the next poll or the next launch
  // (NEWS-340). Cleared locally first so the banner goes on the click rather
  // than up to four seconds later.
  void delegate(root, 'click', '[data-action=dismiss-quarantine]', () => {
    appStore.actions.setQuarantine(null);
    void dismissQuarantine();
  });
  // The warning is derived from the runs list, so dismissal is by run id — a
  // later, different failure has a new id and shows again.
  void delegate(root, 'click', '[data-action=dismiss-warn]', (_e, el) => {
    const id = el.getAttribute('data-run-id');
    if (id !== null) appStore.actions.dismissRun(id);
  });
  // Notification toggle. Enabling requires a permission grant, and the request
  // must ride the user gesture that is this change event.
  /**
   * Turn notifications on from a user gesture: ask for permission, and only if
   * granted persist the setting and subscribe this browser for closed-app push
   * (NM-251). Shared by the Settings toggle and the re-engagement nudge's
   * button (NM-272), so the two cannot drift. Returns whether it is now on;
   * `notifyPermissionDenied` records a refusal for both surfaces to show.
   */
  async function turnOnNotifications(): Promise<boolean> {
    const granted = await ensureNotificationPermission();
    appStore.actions.setNotifyPermissionDenied(!granted);
    if (!granted) return false;
    // Persist only when we can actually deliver — otherwise the setting would
    // read "on" while nothing ever fires.
    await setNotifyOnNewItems(true);
    // …and, when the deploy has Web Push configured, subscribe this browser for
    // **closed-app** notifications too (NM-251). Best-effort and behind the same
    // permission grant: it never blocks the foreground toggle, and a deploy
    // without VAPID keys (`pushPublicKey` null) simply doesn't offer it.
    const pushKey = appStore.state.value.pushPublicKey;
    if (pushKey !== null) await enablePush(pushKey).catch(() => undefined);
    return true;
  }

  void delegate(root, 'change', '[data-action=notify-toggle]', (_e, el) => {
    // The native Switch input's `checked` property is the live state.
    const wantsOn = Reflect.get(el, 'checked') === true;
    if (!wantsOn) {
      appStore.actions.setNotifyPermissionDenied(false);
      void setNotifyOnNewItems(false);
      // Also drop the closed-app push subscription (NM-251), best-effort.
      void disablePush().catch(() => undefined);
      return;
    }
    void (async () => {
      const on = await turnOnNotifications();
      if (!on) {
        // The user's click already checked the box. The stored setting stays
        // false, but false→false is no attribute change, so morph won't reset
        // the live `checked` property — do it by hand.
        Reflect.set(el, 'checked', false);
      }
    })();
  });
  // "Not now": advance the 15 → 30 → 90 → never schedule for this device.
  //
  // **Registered before the enable delegate, deliberately** (the morph
  // node-reuse double-fire, see AGENTS.md). A refusal re-renders the nudge from
  // its `ready` shape [icon, text, enable, dismiss] to `blocked` [icon, text,
  // dismiss] *during the same click's dispatch* — the permission promise is
  // already settled, so the microtask checkpoint between listeners runs the
  // store update and the morph — and the morph reuses the enable button's node
  // as the dismiss button. A dismiss delegate registered *after* the enable one
  // then matched that same node for the same click and snoozed the nudge the
  // reader had just tried to act on. In this order it runs first, while the node
  // still says "enable", and nothing matches.
  void delegate(root, 'click', '[data-action=dismiss-push-nudge]', () => {
    appStore.actions.snoozePushNudge();
  });
  // The nudge's button (NM-272): the same path as the toggle. On success the
  // setting flips on and the nudge renders itself away; on refusal
  // `notifyPermissionDenied` turns it into the "blocked" sentence.
  void delegate(root, 'click', '[data-action=nudge-enable-notifications]', () => {
    void turnOnNotifications();
  });

  void delegate(root, 'click', '[data-action=confirm-ok]', () => {
    resolveConfirm(true);
  });
  void delegate(root, 'click', '[data-action=confirm-cancel]', () => {
    resolveConfirm(false);
  });
  // Backdrop click-away cancels. Same nesting caveat as the settings dialog:
  // only a click on the backdrop element itself, not a bubbled one from inside.

  /*
   * Exports (NEWS-157, reworked NM-101). Fetch → blob → save, so the control can
   * disable + read "Exporting…" while it runs and a toast can report success or
   * failure — feedback a native `<a download>` could not give. `runExport` closes
   * the stories export dialog on success.
   */
  void delegate(root, 'click', '[data-export-url]', (_e, el) => {
    const url = el.getAttribute('data-export-url');
    if (url !== null && url !== '') void runExport(url);
  });

  void delegate(root, 'click', '[data-action=open-export]', () => {
    appStore.actions.openExport();
  });

  void delegate(root, 'click', '[data-action=close-export]', () => {
    appStore.actions.closeExport();
  });

  // The export dialog's choices (kerf UI `ChoiceChips` radios and a `Select`).
  void delegate(root, 'change', '[data-action=export-scope]', (_e, el) => {
    const scope = (el as HTMLInputElement).value;
    if (scope === 'all' || scope === 'saved' || scope === 'topic') appStore.actions.patchExport({ scope });
  });

  void delegate(root, 'change', 'kui-select[name=export-topic]', (_e, el) => {
    appStore.actions.patchExport({ topicId: (el as HTMLSelectElement).value });
  });

  void delegate(root, 'change', '[data-action=export-format]', (_e, el) => {
    const format = (el as HTMLInputElement).value;
    if (format === 'md' || format === 'json') appStore.actions.patchExport({ format });
  });
}

/**
 * Global interactions that aren't scoped to one element: dismissing the
 * selection and menu, and the Delete key.
 */
/** An open kerf UI menu, select or dialog: Escape inside one is that component's. */
const OPEN_POPUP_SELECTOR =
  '.kui-popup-menu:has(> .kui-popup-menu__trigger[aria-expanded="true"]), kui-select[open], dialog[open]';

function wireGlobalKeysAndDismiss(): void {
  document.addEventListener('mousedown', (e) => {
    if (!(e.target instanceof Element)) return;
    // A click on a row, or inside the menu, is handled by its own delegate.
    if (e.target.closest('[data-topic-row]') !== null) return;
    // A topic menu (a `PopupMenu`, its panel in the top layer) acts on the
    // selection, so a press inside it must not clear that selection first.
    if (e.target.closest('[data-component="popup-menu"]') !== null) return;
    // The Disabled group header (NM-201) lives *inside* the topic list, so
    // clearing the selection here would re-morph the list and detach the very
    // button under the pointer before its click could land. Its own control acts
    // on the group, not the selection, so leave the selection alone.
    if (e.target.closest('[data-topic-section]') !== null) return;
    const { selectedTopicIds } = appStore.state.value;
    if (selectedTopicIds.length > 0) appStore.actions.setSelection([]);
  });

  document.addEventListener('keydown', (e) => {
    // Tab is trapped inside an open dialog (NEWS-90): without it, tabbing walks
    // out of the modal into the page behind, which a screen-reader user cannot
    // see is still there.
    if (e.key === 'Escape') {
      // Escape inside an *open* kerf UI menu, select or dialog is that
      // component's: it closes itself, and the ladder below must not also act.
      // (Focus returns to a closed menu's trigger after a choice; Escape there
      // belongs to the ladder again.) An open menu is the one whose trigger
      // says `aria-expanded="true"`.
      if (e.target instanceof Element && e.target.closest(OPEN_POPUP_SELECTOR) !== null) return;
      const s = appStore.state.value;
      if (s.confirm !== null) {
        resolveConfirm(false);
        return;
      }
      // The capacity dialog (NM-201) opens over the main view, dismissible like the
      // rest — the enable it was gating simply doesn't happen.
      if (s.capacityDialog !== null) {
        appStore.actions.closeCapacity();
        return;
      }
      // The upgrade checkout (NM-225/226) — a modal over the main view; Escape is
      // Cancel (no charge was made unless it already succeeded and closed itself).
      if (s.checkout !== null) {
        appStore.actions.closeCheckout();
        return;
      }
      // Innermost first: a dialog opened over another closes alone. The
      // Edit-topic dialog (which since NM-27 carries the focus chips that used to
      // have their own Escape-closable guidance dialog) is one of these.
      if (s.renameTopicId !== null) {
        appStore.actions.closeRename();
        return;
      }
      // The disambiguation sense picker (NM-163) — a standalone dialog over the
      // main view, dismissible like the rest; the topic stays "needs attention"
      // until a sense is chosen, so dismissing loses nothing.
      if (s.sensePickTopicId !== null) {
        appStore.actions.closeSensePicker();
        return;
      }
      // The did-you-mean dialog (NM-184): Escape dismisses it like Cancel, which
      // also removes the pending placeholder it was deciding.
      if (s.topicSuggest !== null) {
        dismissSuggest();
        return;
      }
      if (s.pulseDetailOpen) {
        appStore.actions.closePulse();
        return;
      }
      // Above settings — the export dialog opens over the Data tab (NEWS-158),
      // so Escape must close it and leave Settings standing.
      if (s.export !== null) {
        appStore.actions.closeExport();
        return;
      }
      if (s.settingsOpen) {
        closeSettings();
        return;
      }
      // Above onboarding, because discovery now opens *over* the Topics step
      // (NEWS-146). Without this rung Escape closed the wizard underneath and
      // left discovery floating on top of nothing.
      if (s.discover !== null) {
        appStore.actions.closeDiscover();
        return;
      }
      if (s.onboarding !== null && s.onboarding !== 'auto') {
        // Closing the *last* screen by any means implies its primary action —
        // Start watching / Keep current topics — never a silent discard (NM-245).
        // The last step no longer has a Close control (it is a Back button now),
        // so Escape is the "any other way" to leave it. Earlier steps still
        // discard on Escape, which is what "Skip setup" does there.
        if (s.onboarding === ONBOARDING_STEPS[ONBOARDING_STEPS.length - 1]) finishOnboarding();
        else closeOnboarding();
        return;
      }
      // An expanded story card (NEWS-281) is page content rather than an
      // overlay, so it closes *after* every dialog — and after the menus, since
      // a menu opens over a card — but before the selection rung, so one press
      // does one thing.
      if (s.expandedItemId !== null) {
        appStore.actions.collapseExpandedItem();
        return;
      }
      appStore.actions.setSelection([]);
      return;
    }
    if (e.key !== 'Delete' && e.key !== 'Backspace') return;
    // Never steal Backspace from a text field — deleting a character must not
    // delete a topic — and never act from inside an open dialog at all
    // (NM-ZJT0H7, NM-RR1Q7G).
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
    if (active instanceof Element && active.closest('dialog[open], [contenteditable="true"]') !== null) return;
    const { selectedTopicIds } = appStore.state.value;
    if (selectedTopicIds.length === 0) return;
    e.preventDefault();
    confirmDelete(selectedTopicIds);
  });
}

/** Start the long-poll: each refresh waits on the server until the state revision
 * moves or the hold times out, and the loop re-issues the next when it resolves. */
function startLongPoll(): () => void {
  return startStatePolling(browserPollDeps((signal) => refreshState({ wait: true, signal })));
}

/**
 * Start live updates with the right transport for this deploy (NM-231, NM-250).
 *
 * Two ways to be told the server has fresh data:
 * - **Long-poll** (dev/local, and the default): a held `/api/state?waitRev=`
 *   released when the state revision moves. `poll.ts` owns the *scheduling* so it
 *   is unit-testable (NEWS-238); this is only what a refresh means here.
 * - **RTDB signal** (`state.signal` present, Cloud Run): wait on Firebase over an
 *   `EventSource` and only call the API when a per-account marker moves, so a
 *   held connection never bills API compute (`signal-transport.ts`). On repeated
 *   Firebase failure it falls back to the long-poll, so a reader always updates.
 *
 * Called after the first `refreshState()`, because whether a signal channel
 * exists is something `/api/state` tells us (and the first load already made the
 * app current, so the transport can treat the stream's baseline as seen).
 */
function startLiveUpdates(): () => void {
  const signal = appStore.state.value.signal;
  if (signal === null || typeof EventSource === 'undefined') return startLongPoll();
  return startSignalTransport(signal, {
    refresh: () => {
      void refreshState();
    },
    isVisible: () => document.visibilityState === 'visible',
    onVisibilityChange: (listener) => {
      document.addEventListener('visibilitychange', listener);
      return () => {
        document.removeEventListener('visibilitychange', listener);
      };
    },
    createEventSource: (url) => new EventSource(url),
    // Firebase is unreachable — keep the reader updating over the long-poll.
    onFailure: () => {
      stopPoll = startLongPoll();
    },
  });
}

/** Is the app actually in front of the user right now? */
function isForegrounded(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

/**
 * Heartbeat that permits scheduled checks on subscription-backed providers.
 *
 * Sent on an interval comfortably shorter than the server's window, plus
 * immediately on the events that can make the app foregrounded, so returning
 * to it takes effect at once instead of after the next tick.
 */
function startForegroundHeartbeat(): () => void {
  const beat = (): void => {
    if (isForegrounded()) void reportForeground();
  };
  beat();
  const handle = window.setInterval(beat, 60_000);
  window.addEventListener('focus', beat);
  document.addEventListener('visibilitychange', beat);
  // Stoppable (NM-41): on sign-out this must go quiet, or it keeps posting
  // `/api/foreground` at a session that is no longer signed in.
  return () => {
    window.clearInterval(handle);
    window.removeEventListener('focus', beat);
    document.removeEventListener('visibilitychange', beat);
  };
}

/**
 * Compile-time flag, substituted by esbuild (`--define:__KERF_DEV__`). False in
 * `build:client`, true in `build:client:dev`.
 *
 * A literal `false` lets esbuild drop the whole block *and* the `import()` with
 * it, so the dev chunk is never emitted in a production build — that is the
 * point of a define rather than a runtime check. It cannot be
 * `process.env.NODE_ENV`, kerf's own documented form: this is an IIFE browser
 * bundle, `process` doesn't exist, and the read would throw at startup.
 */
declare const __KERF_DEV__: boolean;

/**
 * kerf development diagnostics (NEWS-100). kerf 3.x no longer infers dev mode —
 * installing these is the app's decision, made here.
 *
 * `invariants: 'throw'` is the valuable one: it audits kerf's list bookkeeping
 * against the live DOM after every render and fails at the render that broke
 * it, rather than leaving a wrong picture to be discovered several interactions
 * later. That is exactly the shape KF-377 had.
 *
 * `enableWarnings()` rather than the `KERF_DEV_WARN_*` env vars, which cannot
 * be reached from a browser at all.
 */
if (__KERF_DEV__) {
  void import('kerfjs/dev').then((dev) => {
    dev.enableWarnings({
      invariants: 'throw',
      staleBinding: true,
      narrowSet: true,
      duplicateEachKeys: true,
      listRebind: true,
      staleIndex: true,
      eachInMorphSkip: true,
      delegateInEffect: true,
      rebuiltListeners: true,
      parserRepair: true,
    });
    return dev;
  });
}

/**
 * Put the chosen theme on `<html>`, where the stylesheet reads it (FR-3.75).
 *
 * `auto` **removes** the attribute: following the system is the
 * `prefers-color-scheme` query doing its job, and `data-theme="auto"` would be a
 * third state every rule had to exclude.
 *
 * Exported-shaped rather than inlined so the settings handler and the poll can
 * both use it — a restore, or a second window changing the setting, arrives
 * through `/api/state` rather than through the control.
 */
function applyTheme(theme: 'auto' | 'light' | 'dark'): void {
  if (theme === 'auto') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', theme);
}

/**
 * Sign-in gate boot (NM-20, phase 3).
 *
 * `ensureAppRunning` starts the foreground heartbeat exactly once — the moment we first enter the app with an active
 * account. Nothing here runs while signed out, so a gated page never polls
 * `/api/state` or mounts the app.
 */
let appRunning = false;
let liveUpdatesRunning = false;
let stopPoll: (() => void) | null = null;
let stopHeartbeat: (() => void) | null = null;
function ensureAppRunning(): void {
  if (appRunning) return;
  appRunning = true;
  stopHeartbeat = startForegroundHeartbeat();
}

/**
 * Start live updates once (NM-250), after the first `refreshState()` — because
 * which transport to use (long-poll vs the RTDB signal) is read from
 * `state.signal`, which the first `/api/state` answer carries. Latched separately
 * from `ensureAppRunning` so it never double-starts and can be released on
 * sign-out with the rest.
 */
function ensureLiveUpdates(): void {
  if (liveUpdatesRunning) return;
  liveUpdatesRunning = true;
  stopPoll = startLiveUpdates();
}

/**
 * Stop the background timers and release the `appRunning` latch (NM-41).
 *
 * `ensureAppRunning` starts the 4s state poll and the 60s foreground heartbeat
 * exactly once and latches so they never double-start. On sign-out that latch
 * has to release and the timers stop, or they keep firing `/api/state`,
 * `/api/items` and `/api/foreground` at a session that is no longer signed in —
 * every request failing and filling the console, with no way to stop short of
 * closing the tab. Resetting the latch also lets a later sign-in start clean.
 */
function stopAppRunning(): void {
  if (!appRunning) return;
  appRunning = false;
  liveUpdatesRunning = false;
  stopPoll?.();
  stopHeartbeat?.();
  stopPoll = null;
  stopHeartbeat = null;
}

/** Enter the app for an active, registered account: start it, refresh, decide onboarding. */
async function enterActiveApp(): Promise<void> {
  ensureAppRunning();
  await refreshState();
  // After the first load, because `state.signal` decides the transport (NM-250).
  ensureLiveUpdates();
  maybeOpenOnboarding();
}

/**
 * A validated same-origin `?return=` path (NM-266), or null. Lets the admin console
 * (and any deep link) route sign-in through the **one** SPA gate and land back where
 * it started: on becoming active we navigate there instead of entering the reader
 * app. Only a relative `/…` path (never `//`, which is protocol-relative) is honored,
 * so it can never become an open redirect.
 */
function pendingReturnPath(): string | null {
  try {
    const raw = new URLSearchParams(window.location.search).get('return');
    if (raw !== null && raw.startsWith('/') && !raw.startsWith('//')) return raw;
  } catch {
    /* no query / no URL — fall through */
  }
  return null;
}

/** Enter the app for an active account, unless a `?return=` deep link should win (NM-266). */
async function enterOrReturn(): Promise<void> {
  const back = pendingReturnPath();
  if (back !== null) {
    window.location.assign(back);
    return;
  }
  await enterActiveApp();
}

/** Learn who is signed in, and enter the app when it's an active account. */
async function bootSession(): Promise<void> {
  try {
    const resp = await fetchSession();
    appStore.actions.setSession(resp);
    if (sessionGateState(resp).status === 'active') await enterOrReturn();
  } catch {
    // An unreachable/failed session probe falls back to the gate — the safe
    // default, and the user can retry the sign-in from there. Preserve the
    // deploy-constant `appleSignIn` (NM-257) so the Apple button doesn't vanish.
    appStore.actions.setSession(signedOutSession(appStore.state.value.session));
  }
}

/** Open the dev chooser popup, then re-read the session and enter the app if active. */
async function doSignIn(root: HTMLElement, signIn: () => Promise<void> = signInGoogle): Promise<void> {
  appStore.actions.setSignInError(null);
  try {
    await signIn();
  } catch (err) {
    // A blocked pop-up is the common failure; show it on the gate rather than
    // failing silently.
    appStore.actions.setSignInError(err instanceof Error ? err.message : String(err));
    return;
  }
  const resp = await fetchSession();
  appStore.actions.setSession(resp);
  if (sessionGateState(resp).status === 'active') await enterOrReturn();
}

/**
 * Register the signed-in identity and run the onboarding wizard as the setup
 * (NM-20, phase 3).
 *
 * Order matters: register **first** so the fresh account exists and its session
 * is re-issued, *then* start the app and open onboarding — whose profile,
 * location and topic writes now land in the new account rather than the default.
 * `setOnboarding('welcome')` is explicit rather than `maybeOpenOnboarding`
 * because registration *is* the setup: the per-device "seen" flag (shared across
 * accounts on one database) must not suppress it.
 */
async function completeRegistration(): Promise<void> {
  appStore.actions.setSignInError(null);
  try {
    await registerAccount();
    const resp = await fetchSession();
    appStore.actions.setSession(resp);
    if (sessionGateState(resp).status === 'active') {
      ensureAppRunning();
      await refreshState();
      ensureLiveUpdates();
      appStore.actions.setOnboarding('welcome');
    }
  } catch (err) {
    appStore.actions.setSignInError(err instanceof Error ? err.message : String(err));
  }
}

/** Sign out and return to the gate (NM-20, phase 3). */
async function doSignOut(): Promise<void> {
  // Stop the background poll and heartbeat first (NM-41). They were started on
  // entering the app and latched on; if they keep running they hammer
  // `/api/state`, `/api/items` and `/api/foreground` at the now-signed-out
  // session, every request failing, until the tab is closed.
  stopAppRunning();
  // Close the Settings dialog (and any other open modal) before flipping the
  // session (NM-39). Sign-out is reached *from* the Settings dialog, so
  // `settingsOpen` is true — and it is only client state, which the gate does not
  // render but the *next* session does. Left set, it re-appears layered over
  // whatever the new session shows: the reporter signed out, created a new
  // account, and the setup guide rendered underneath a stale Settings dialog.
  appStore.actions.setSettingsOpen(false);
  appStore.actions.closeDiscover();
  appStore.actions.closeConfirm();
  appStore.actions.closeRename();
  try {
    await logoutSession();
  } catch {
    // Best-effort: clearing the cookie server-side is nice-to-have; the gate is
    // driven by the session we now set to signed-out either way.
  }
  // Preserve `appleSignIn` (NM-257) so the gate keeps the Apple button after
  // sign-out — a bare `{ user: null }` drops the deploy-constant flag.
  appStore.actions.setSession(signedOutSession(appStore.state.value.session));
}

/**
 * Open every rendered dialog that is not open yet. The store decides which
 * dialogs exist (each renders while its state is set); `openDialog` makes one
 * modal. Opening an open dialog does nothing, and a dialog the store dropped
 * is simply gone from the DOM, so this needs no bookkeeping of its own.
 */
function syncDialogs(root: HTMLElement): void {
  for (const dialog of root.querySelectorAll<HTMLDialogElement>('dialog[data-dialog]:not([open])')) openDialog(dialog);
}

const root = document.getElementById('app');
if (root) {
  void syncColorScheme(document, window, (dark) => {
    schemeDark.value = dark;
  });
  mount(root, () => rootJsx());
  // A different query is a fresh list (FR-16.3). Observe view state rather than
  // individual controls so polls, Show More and story edits preserve position.
  let lastFeedView = feedViewKey(appStore.state.value);
  effect(() => {
    const view = feedViewKey(appStore.state.value);
    if (view === lastFeedView) return;
    lastFeedView = view;
    queueMicrotask(() => {
      root.querySelector('section[aria-label="Stories"]')?.scrollTo({ top: 0, behavior: 'instant' });
    });
  });
  // A `<select>` the user has touched stops following the `selected` attribute
  // a morph writes (NEWS-238) — and kerf can only re-sync the property when
  // that attribute *changes*, so a control whose rendered choice stays put
  // never comes back. Registered after `mount` and deferred a microtask so it
  // runs once the morph for this change has finished, whatever order the
  // effects were queued in. See `select-sync.ts`.
  effect(() => {
    void appStore.state.value;
    // Also fire once per poll even when state was unchanged (NM-230): the
    // skip-when-unchanged there stops the tree re-rendering on an idle tick, but
    // a moved-then-unpersisted control still has to be snapped back to the
    // server value, and this pass — form controls only — is what does it.
    void pollTick.value;
    queueMicrotask(() => {
      // A rendered dialog opens here (NM-ZFF2A0): kerf UI's `DialogSurface` is
      // a native `<dialog>`, opened imperatively, so the store's "this dialog
      // is showing" becomes `openDialog` once the morph has put it in the DOM.
      syncDialogs(root);
      syncSelects(root);
      // Radios have the same dirty-flag drift as a `<select>` (NM-25): once the
      // user moves the selection, a morph that leaves the `checked` attribute
      // where it was no longer re-syncs the property. The theme tiles are radios.
      syncRadios(root);
    });
  });
  // Push the topic hold the instant its edit dialog opens or closes (NM-231).
  // The hold rides the `/api/state` poll's `?holding=` param (NEWS-366), and the
  // long poll only re-issues every ~10s — so without this, opening a dialog
  // would not name its topic to the server for up to a full timeout, and a sweep
  // in that window could check a topic the user is editing. Firing an immediate
  // (non-waiting) refresh on the *transition* sends the new holding state at
  // once. Guarded on the id actually changing so it fires on open/close only,
  // not on every state change — the refresh it triggers cannot re-trigger it.
  let lastHeldTopicId: string | null = appStore.state.value.renameTopicId;
  effect(() => {
    const held = appStore.state.value.renameTopicId;
    if (held === lastHeldTopicId) return;
    lastHeldTopicId = held;
    void refreshState();
  });
  // The theme follows the *setting*, not only the control (FR-3.75, NEWS-334).
  // A restore from backup brings someone else's choice, and a second window can
  // change it — both arrive through `/api/state`, never through the `<select>`
  // on this page. The server stamps the attribute on first paint; this keeps it
  // true afterwards.
  effect(() => {
    applyTheme(appStore.state.value.settings.theme);
  });
  wireEvents(root);
  wireGlobalKeysAndDismiss();
  // Gate on sign-in first (NM-20, phase 3): learn who is signed in, and only
  // then — for an active account — load `/api/state`, start the poll, and decide
  // onboarding. Provider configuration is environment-only (NM-12), so nothing
  // else waits here. A signed-out or unregistered user never reaches the app.
  void bootSession();
}
